// English (US) translations.
// Keep keys in lockstep with zh-CN.ts — add to both when adding new strings.
export default {
  common: {
    loadFailed: 'Failed to load',
    deleted: 'Deleted',
    save: 'Save',
    cancel: 'Cancel',
    confirm: 'Confirm',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    refresh: 'Refresh',
    submit: 'Submit',
    loading: 'Loading…',
    empty: 'No data',
    actions: 'Actions',
    status: 'Status',
    name: 'Name',
    type: 'Type',
    yes: 'Yes',
    no: 'No',
    on: 'On',
    off: 'Off',
    search: 'Search',
    copy: 'Copy',
    copied: 'Copied',
    back: 'Back',
    next: 'Next',
    more: 'More',
  },
  app: {
    switchLanguage: 'Switch to 中文',
    title: 'cdnX Console',
    logout: 'Logout',
    fullscreen: 'Full screen',
    exitFullscreen: 'Exit full screen',
    openMenu: 'Open menu',
    collapseMenu: 'Collapse menu',
    expandMenu: 'Expand menu',
    foldMenu: 'Collapse',
    search: 'Search',
    searchPlaceholder: 'Search by page name or site domain',
    searchHint: 'Type a page or site name; press / to open from anywhere',
    searchNoHit: 'No matches',
    searchPage: 'Page',
    searchSite: 'Site',
    notifications: 'Notifications',
    toDashboard: 'Open the dashboard',
    pageSettings: 'Customize',
  },
  navbar: {
    store: 'Plans',
    billing: 'Billing',
    tickets: 'Tickets',
    accountId: 'Account ID {id}',
    accountIdFull: 'Account ID: {id}',
    subAccountId: 'Sub-user · Account ID {id}',
    subUser: 'Sub-user',
    subOwner: 'Main account {name} (ID {id})',
    rnIndividual: 'Individual',
    rnEnterprise: 'Enterprise',
    rnPending: 'Under review',
    rnRejected: 'Not verified',
    rnNone: 'Unverified',
    rnGo: 'Verify',
    balance: 'Available (CNY)',
    showBalance: 'Show balance',
    hideBalance: 'Hide balance',
    topUp: 'Top up',
    monthSpent: 'This month: {n}',
    updatedAt: 'Updated {t}',
    unpaid: 'Unpaid',
    toRenew: 'To renew',
    ticketsTodo: 'Replies',
    unread: 'Unread',
    renewals: 'Renewals',
    myOrders: 'My orders',
    invoiceable: 'Invoiceable (CNY)',
    cashable: 'Withdrawable (CNY)',
    toFinance: 'Go to Finance',
  },
  nav: {
    dashboard: 'Dashboard',
    domains: 'Site List',
    rules: 'Rules',
    domain: 'Site',
    login: 'Login',
  },
  register: {
    title: 'Create account',
    subtitle: 'Create a new account',
    email: 'Email',
    emailUnverified: 'This system has no email capability yet, so no verification message will be sent.',
    confirmPassword: 'Confirm password',
    passwordHelp: 'At least {n} characters.',
    passwordMismatch: 'The two passwords do not match',
    passwordTooShort: 'Password must be at least {n} characters',
    submit: 'Sign up',
    success: 'Registered — please sign in',
    failed: 'Registration failed',
    closed: 'Self-service registration is not open on this platform. Contact an administrator for an account.',
    backToLogin: 'Back to sign in',
    usernamePlaceholder: 'Username',
    usernameRequired: 'Enter a username',
    emailPlaceholder: 'Email',
    emailRequired: 'Enter your email',
    emailCodePlaceholder: 'Email code',
    emailCodeRequired: 'Enter the 6-digit email code',
    phonePlaceholder: 'Mobile number',
    phoneRequired: 'Enter your mobile number',
    smsCodePlaceholder: 'SMS code',
    passwordPlaceholder: '{min}+ chars, two of letters/digits/symbols',
    passwordRequired: 'Set a password',
    confirmPlaceholder: 'Confirm password',
    welcome: 'Your account is ready — welcome',
  },
  ipLists: {
    title: 'IP Lists',
    introAdmin: 'Platform-wide allow and deny lists. Entries here apply to every node and every site, and are pushed into nftables for kernel-level blocking.',
    introUser: 'Account-level allow and deny lists. Entries here apply to every site you own.',
    searchPlaceholder: 'Search address or note',
    checkEmpty: 'Cannot be empty',
    checkBadAddress: 'Not a valid IP address',
    checkBadCidr: 'Not a valid network',
    checkTooBroad: '/{bits} is too broad; the minimum is /{min}',
    checkDuplicate: 'Duplicate of a line above',
    problemsHead: '{n} problem(s) to fix before this can be submitted:',
    lineNo: 'line {n}',
    fixProblemsFirst: 'Fix the problems listed first',
    batchAddedSkipped: 'Added {n}; {s} were already listed and were skipped',
    modeSingle: 'Single',
    modeBatch: 'Bulk',
    bulkPlaceholder: 'One per line, e.g.\n1.2.3.4\n10.0.0.0/8\n2001:db8::/32',
    bulkHelp: '{n} recognised, up to {max} per batch. Commas work as separators too.',
    batchAdded: 'Added {n}',
    addDeny: 'Add to deny list',
    addAllow: 'Add to allow list',
    targetRequired: 'Enter an IP address or network',
    noMatch: 'Nothing matches that search',
    empty: 'No entries yet',
    tabDeny: 'Deny list',
    tabAllow: 'Allow list',
    enforceKernel: 'Kernel',
    enforceEdge: 'Edge',
    enforcementCol: 'Enforced at',
    enforcementNote: 'Kernel blocking drops the packet in nftables, so attack traffic costs the node no TLS handshake and almost no CPU — but it applies to the whole node, because the kernel has no idea which site a packet was for. It covers traffic to ports 80/443 only, so SSH and the link between the node and its control plane are never cut. Edge blocking refuses the request in the proxy, after the handshake.',
    userKernelHint: 'Requests from a denied address are refused at the edge and never reach your origin.',
    tabAuto: 'Automatic bans',
    allowNote: 'The allow list wins over everything: an address on it is never stopped by a deny list, a CC ban, the WAF or human verification. Use it for monitoring probes, payment callbacks and anything else that must not be caught by a broad block.',
    autoNote: 'Addresses banned automatically by CC protection, already pushed to the node kernel with an expiry. The kernel clears them on its own; releasing one early also queues a task telling that node to remove it.',
    activeOnly: 'Active only',
    add: 'Add',
    added: 'Added',
    addFailed: 'Could not add this entry',
    targetPlaceholder: 'IP or network, e.g. 1.2.3.4 or 10.0.0.0/8',
    notePlaceholder: 'Note (optional)',
    targetCol: 'Address',
    noteCol: 'Note',
    addedCol: 'Added',
    ipCol: 'IP',
    nodeCol: 'Node',
    domainCol: 'Domain',
    ruleCol: 'Rule',
    bannedAtCol: 'Banned at',
    expiresCol: 'Expires',
    release: 'Release',
    released: 'Released',
    releaseQueued: 'Released; the node has been told to remove it',
    releaseRecorded: 'Release recorded, but the node could not be notified — the kernel ban clears when it expires',
    releaseFailed: 'Could not release this ban',
    confirmDeleteTitle: 'Delete this entry?',
    confirmDelete: '{target} will be removed from the list and the config republished to the nodes.',
    statTotal: 'Entries',
    statCap: 'up to {n}',
    statToday: 'Added today',
    listCount: '{n} in all',
    typeCol: 'Type',
    typeIp: 'IP',
    typeCidr: 'Range',
  },
  auth: {
    createAccount: "Don't have an account? Sign up",
    welcomeBack: 'Welcome back',
    loginFailed: 'Login failed',
    signInSubtitle: 'Sign in to the {name} control plane',
    username: 'Username',
    password: 'Password',
    usernamePlaceholder: 'admin',
    passwordPlaceholder: 'Password',
    signIn: 'Sign in',
    unauthorized: 'Unauthorized or session expired, please sign in again',
    mfaLabel: 'Verification code',
    mfaHint: 'Enter the 6-digit code from your authenticator app',
    mfaVerify: 'Verify',
    mfaBack: 'Back to sign in',
    smsLabel: 'Text message code',
    smsHint: 'The code goes to {phone}',
    smsSentHint: 'Code sent to {phone}; it holds for {minutes} minutes',
    useSms: 'Use a text message code instead',
    useMfa: 'Use the MFA code instead',
    forgot: 'Forgot password',
    resetSubtitle: 'Reset your password with your verified phone',
    resetCodeHint: 'The code goes to the verified phone of this account',
    resetSubmit: 'Reset password',
    resetDone: 'Password reset. Sign in with the new password',
    resetFailed: 'Reset failed',
    backToSignIn: 'Back to sign in',
    welcome: 'Welcome back',
    secondTitle: 'Verify your sign-in',
    secondHint: "To keep your account secure, please confirm it's you",
    resetTitle: 'Reset password',
    identityPlaceholder: 'Phone, email or username',
    identityRequired: 'Enter your phone, email or username',
    passwordRequired: 'Enter your password',
    remember: 'Stay signed in for 7 days',
    noAccount: 'No account yet?',
    registerNow: 'Sign up',
    backToSite: 'Official site',
    otherLanguage: '中文',
    agreeNotice: 'By signing in you agree to the ',
    agreeConsent: 'I have read and agree to the ',
    agreeAnd: ' and ',
    terms: 'Terms of Use',
    privacy: 'Privacy Policy',
    termsTitle: 'Terms of Use',
    privacyTitle: 'Privacy Policy',
    agreementOk: 'Got it',
    agreementAgree: 'Agree',
    agreementFailed: 'The agreement could not be loaded — try again later',
    noticeClose: 'Close the notice',
    tabPassword: 'Password',
    tabCode: 'Code',
    codeTarget_phone: 'Mobile number',
    codeTarget_email: 'Email',
    codeTarget_any: 'Mobile number or email',
    codeTargetRequired_phone: 'Enter your mobile number',
    codeTargetRequired_email: 'Enter your email',
    codeTargetRequired_any: 'Enter your mobile number or email',
    otherWays: 'Other ways to sign in',
    waySub: 'Sub-user',
    wayPasskey: 'Passkey',
    subTitle: 'Sub-user sign-in',
    subHint: 'Sign in as a sub-user your main account created',
    subOwnerPlaceholder: 'Main account ID or username',
    subNamePlaceholder: 'Sub-user name',
    subOwnerRequired: 'Enter the main account ID or username',
    subNameRequired: 'Enter the sub-user name',
    subBack: 'Back to main account sign-in',
    passkeyCancelled: 'Passkey sign-in cancelled',
    passkeyBlocked_browser: "This browser doesn't support passkeys. Update it or sign in another way",
    passkeyBlocked_domain: 'Passkeys need the console opened on a domain over HTTPS; this address cannot use them',
  },
  dashAccount: {
    balance: 'Balance (CNY)',
    spent: 'This month (CNY)',
    updated: 'As of {time}',
    orders: 'Orders',
    ledger: 'Ledger',
    invoices: 'Invoices',
    packages: 'Plans',
    unpaid: 'Unpaid orders',
    toRenew: 'Packages to renew',
    ticketsTodo: 'Tickets awaiting you',
    invoiceable: 'Invoiceable (CNY)',
    cashable: 'Withdrawable (CNY)',
    phoneBound: 'Phone bound',
    phoneUnbound: 'No phone',
    guarded: '2-step sign-in',
    unguarded: 'No 2-step sign-in',
    tipRenewShort: 'Auto-renew failed: balance short',
    tipRenewFailed: 'A package failed to auto-renew',
    tipAutoRenew: 'Turn on auto-renew',
    tipGuard: 'Turn on sign-in protection',
    topUp: 'Top up',
    view: 'Take a look',
    setUp: 'Set it up',
  },
  dashboard: {
    welcome: 'Welcome back, {name}',
    sites: 'Sites',
    unitSites: 'sites',
    hostnames: 'Hostnames',
    unitHostnames: 'names',
    certs: 'Certificates',
    unitCerts: 'certs',
    certsDue: '{n} due for renewal',
    subscriptions: 'Packages',
    unitSubs: 'active',
    subsDue: '{n} expiring',
    trafficUsed: 'Traffic',
    requestsUsed: 'Requests',
    usageOf: '{pct} used',
    usageUnlimited: 'Unlimited',
    usageNone: 'No active package',
    today: 'Today',
    trend: 'Usage',
    trendTotal: '{total} in the last {days} days',
    trendPeak: 'Peak {total} in the last {days} days',
    metric_traffic: 'Traffic',
    metric_requests: 'Requests',
    metric_bandwidth: 'Peak bandwidth',
    series_traffic: 'Traffic',
    series_requests: 'Requests',
    series_bandwidth: 'Peak bandwidth',
    last7: 'Last 7 days',
    last30: 'Last 30 days',
    viewMore: 'More',
    noLogs: 'Log storage is not connected, so there is no usage data yet',
    topSites: 'Top 10',
    topUnavailable: 'Log storage is not connected, so there are no rankings yet',
    siteStatus: 'Site status',
    siteActive: 'Serving',
    siteDraft: 'Setting up',
    siteSuspended: 'Suspended',
    siteDisabled: 'Disabled',
    certStatus: 'Certificate status',
    certValid: 'Valid',
    certExpiring: 'Expiring',
    certExpired: 'Expired',
    codeStatus: 'Status codes today',
    codeCaption: 'Requests',
    codeOk: '2xx/3xx',
    code4xx: '4xx',
    code5xx: '5xx',
    allClear: 'All clear — nothing needs attention',
    tag_cert: 'Cert',
    tag_site: 'Site',
    tag_sub: 'Package',
    tag_quota: 'Usage',
    sitesSuspended: '{n} suspended',
    certsExpired: '{n} expired',
    certsExpiring: '{n} expiring within 14 days',
    subsExpiring: '{n} expiring within 7 days',
    autoRenewFailed: '{n} failed to renew automatically',
    sitesDraft: '{n} awaiting CNAME',
    trafficHigh: '{n} of traffic used',
    requestsHigh: '{n} of requests used',
    statsChNotConfigured: 'ClickHouse is not configured. Set clickhouse.addr in control.yaml, then run cdnx-migrate ch-up to enable analytics.',
    tag_ticket: 'Ticket',
    ticketsReplied: '{n} with a new reply',
  },
  layout: {
    navbarHeight: 'Bar height',
    menuWidth: 'Menu width',
    colorWeak: 'Color weakness',
    grayscale: 'Grayscale',
  },
  custom: {
    notify: 'Notifications',
    sound: 'New message sound',
    soundDesc: 'A chime when a ticket gets a reply',
    speech: 'Spoken alert',
    speechDesc: 'Reads out “You have a new ticket message” when a reply comes in',
    title: 'Customize',
    theme: 'Theme',
    theme_light: 'Light',
    theme_dark: 'Dark',
    theme_twotone: 'Two-tone',
    navbar: 'Top bar',
    navbar_on: 'Show the top bar',
    navbar_onDesc: 'Search, messages and your account at the top',
    navbar_off: 'Hide the top bar',
    navbar_offDesc: 'More room; open these settings from the corner button',
    menu: 'Side menu',
    menu_on: 'Show the menu',
    menu_onDesc: 'The navigation menu on the left',
    menu_off: 'Hide the menu',
    menu_offDesc: 'Pages take the full width',
    pageTitle: 'Page title',
    pageTitle_on: 'Show page titles',
    pageTitle_onDesc: "The page's name above its content",
    pageTitle_off: 'Hide page titles',
    pageTitle_offDesc: 'No page name, a tighter layout',
    access: 'Accessibility',
    fullscreenDesc: 'The console fills the screen; Esc leaves',
    colorWeakDesc: 'Inverted colours, easier to tell apart',
    grayscaleDesc: 'The whole console in greys',
    reset: 'Restore defaults',
  },
  monitor: {
    title: 'Monitoring',
    bandwidth: 'Peak bandwidth',
    requests: 'Requests',
    traffic: 'Traffic',
    blocked: 'Blocked',
    blockedIps: 'Blocked IPs',
    hits: 'Cache hits',
    range_1h: '1h',
    range_today: 'Today',
    range_yesterday: 'Yesterday',
    range_7d: '7d',
    range_30d: '30d',
    window_1h: 'Last hour',
    window_today: 'Today',
    window_yesterday: 'Yesterday',
    window_7d: 'Last 7 days',
    window_30d: 'Last 30 days',
    vs_1h: 'vs the hour before',
    vs_today: 'vs yesterday so far',
    vs_yesterday: 'vs the day before',
    vs_7d: 'vs the 7 days before',
    vs_30d: 'vs the 30 days before',
    unitTimes: 'times',
    unitIps: 'IPs',
    loadFailed: 'Could not load the monitoring data',
    noLogs: 'Log storage is not connected, so there is no bandwidth, request, traffic, block or cache data yet',
  },
  top: {
    dim_domain: 'Domains',
    dim_url: 'URLs',
    dim_ip: 'IPs',
    dim_country: 'Countries',
    dim_province: 'Provinces',
    dim_status: 'Status',
    dim_isp: 'ISPs',
    dim_referer: 'Referrers',
    direct: 'Direct',
    unknown: 'Unknown',
    requests: 'Requests',
    traffic: 'Traffic',
  },
  stats: {
    title: 'Statistics',
    denyIps: 'IP denylist',
    allowIps: 'IP allowlist',
    credentials: 'API keys',
    cacheJobs: 'Purges today',
    unitEntries: '',
    unitItems: '',
    unitTimes: '',
  },
  sitePanel: {
    title: 'Sites',
    summary: '{active} of {total} serving · today',
    state_active: 'Serving',
    state_draft: 'Setting up',
    state_suspended: 'Suspended',
    state_disabled: 'Disabled',
    aliases: '+{n}',
    aliasesTip: '{n} more alias(es)',
    requests: 'Requests',
    traffic: 'Traffic',
    hitRate: 'Hit rate',
    empty: 'No sites yet.',
    add: 'Add a site',
  },
  packageCard: {
    title: 'My package',
    state_active: 'Active',
    state_expiring: 'Expiring',
    state_expired: 'Expired',
    traffic: 'Traffic this period',
    requests: 'Requests this period',
    unlimited: 'Unlimited',
    expiresAt: 'Expires',
    daysLeft: '{n} days left',
    dueToday: 'Expires today',
    expiredShort: 'Expired',
    resetsAt: 'Period resets',
    sites: 'Sites',
    sitesValue: '{n}',
    none: 'No active package yet. A package is needed before adding a site.',
    expiredNote: 'This package has expired and its sites are suspended. They come back within a minute of renewal.',
    exceededNote: 'This period\'s allowance is used up and its sites are suspended until the next period.',
  },
  siteCerts: {
    daysLeft: '{n} days left',
    expiresToday: 'expires today',
    expiredDays: 'expired {n} days ago',
    hostCount: '{n} hostname(s)',
    intro: 'Each hostname can have its own certificate. Free certificates are issued and renewed by the platform; custom certificates are uploaded on the certificates page and replaced by you before they expire.',
    applyFree: 'Apply for free certificate',
    blockedUnverified: 'A certificate can be requested once the domain passes its CNAME check',
    blockedSiteOff: 'The site is disabled; no certificate can be requested',
    useExisting: 'Use existing certificate',
    hostCol: 'Hostname',
    certCol: 'Certificate',
    typeCol: 'Type',
    statusCol: 'Status',
    expiresCol: 'Expires',
    primary: 'primary',
    typeFree: 'Free',
    typeCustom: 'Custom',
    stateOk: 'OK',
    stateExpiring: 'Expiring soon',
    stateExpired: 'Expired',
    stateMismatch: 'Mismatch',
    stateNone: 'Not configured',
    issuing: 'Issuing',
    issueFailed: 'Issuance failed',
    issueCancelled: 'Issuance cancelled',
    issuingAttempt: 'Issuing (attempt {n}/{max})',
    nextRun: 'Next retry: {t}',
    retryNow: 'Retry now',
    apply: 'Apply',
    change: 'Change',
    choose: 'Choose',
    unbind: 'Unbind',
    unbindTitle: 'Unbind certificate',
    unbindContent: '{host} will fall back to the site\'s newest certificate that covers it; without one, HTTPS will stop working for it.',
    issuer: 'Issuer',
    validity: 'Valid',
    algorithm: 'Algorithm',
    source: 'Source',
    source_bound: 'Chosen',
    source_auto: 'Automatic (the site\'s newest certificate covering this hostname)',
    source_fallback: 'Nothing covers this hostname; the site\'s newest certificate is served for now',
    source_none: '—',
    mismatchHint: 'No certificate covers {host}, so the nodes answer with another of the site\'s certificates and browsers show an error. Apply for or choose one for it.',
    noCert: 'No certificate yet — apply for one or choose an existing one',
    pickHost: 'Choose a hostname',
    pickCert: 'Choose a certificate',
    bindHint: 'Only valid certificates that cover this hostname are listed. Certificates are uploaded on the certificates page.',
    noBindable: 'No valid certificate covers this hostname',
    bound: 'Applied',
    issueQueued: 'Queued — the status appears in the table',
    issueQueueHint: 'Issuance runs in the background queue; you can close this dialog. A failure\'s reason shows in that hostname\'s expanded row.',
    notInPackage: 'Uploads not in package',
  },
  geoBlock: {
    title: 'Region block',
    mode: 'Mode',
    modes: { off: 'Off', block: 'Block selected', allow: 'Allow only selected' },
    modeHint_off: 'No restriction by country or region.',
    modeHint_block: 'Visitors from the selected countries and regions get the action below; everyone else passes.',
    modeHint_allow: 'Only the selected countries and regions pass; everyone else gets the action below.',
    action: 'Action',
    actions: {
      drop: 'Drop (444)',
      deny: 'Block (403)',
      challenge_js: 'JS challenge',
      challenge_slider: 'Slider challenge',
      ban_kernel: 'Kernel ban',
      log: 'Log only',
    },
    actionHint_drop: 'Close the connection without answering. Cheaper than a 403, and tells the client nothing.',
    actionHint_deny: 'Answer with 403, so the visitor can see they were refused.',
    actionHint_ban_kernel: "Drop the first request and hand the IP to the node's firewall (nftables). From then on its packets are dropped in the kernel and cost the proxy nothing.",
    actionHint_challenge_js: 'Visitors must pass a JS challenge first, and are not asked again while the pass lasts.',
    actionHint_challenge_slider: 'Visitors must pass a slider challenge first, and are not asked again while the pass lasts.',
    actionHint_log: 'Block nothing; record a security event. Useful to see what would be affected before enforcing.',
    banSeconds: 'Ban duration (s)',
    banSecondsHint: '60 seconds to 30 days. Lifts on its own; the ban log can lift it early.',
    kernelWarn: 'A kernel ban covers the whole node: the firewall cannot tell which site a packet is for, so a banned IP cannot reach any site on that node, not only this one. Use it only when every site on the node should refuse these regions.',
    regionsBlock: 'Places to block',
    regionsAllow: 'Places allowed',
    levelCountry: 'Countries/regions ({count})',
    levelProvince: 'China provinces ({count})',
    levelCity: 'Cities ({count})',
    rowCountries: 'Countries ({count})',
    rowProvinces: 'Provinces ({count})',
    rowCities: 'Cities ({count})',
    allProvinces: 'All {count} provinces of mainland China',
    useCountryInstead: 'Select China as a whole instead (no City database needed, more accurate)',
    unfold: 'Show all {count}',
    fold: 'Show less',
    levelProvinceAll: 'China provinces (all)',
    provincesCovered: 'All of China is selected, so every province is included. To pick only some provinces, untick China under Countries/regions first.',
    cityCovered: 'All of {country} is selected, so every city in it is included already.',
    summaryAllow: 'Only visitors from {places} can visit; everyone else gets "{action}".',
    summaryBlock: 'Visitors from {places} get "{action}"; everyone else passes.',
    summaryMore: '{list} and {count} places in all',
    nothingSelected: 'Nothing selected yet',
    provinceHint: "Mainland China's 31 provincial-level divisions. Hong Kong, Macao and Taiwan are under Countries/regions.",
    cityPlaceholder: 'City name, e.g. Shenzhen or 深圳',
    addCity: 'Add',
    cityHint: "Matched against the GeoIP database's English or Chinese city name; suffixes such as 市 are optional. Cities of the same name are told apart by the country chosen. At most 200.",
    searchPlaceholder: 'Search by name or code',
    addGreaterChina: '+ Mainland China, HK, Macao, Taiwan',
    selectAll: 'Select all',
    clearAll: 'Clear',
    selectContinent: 'Select continent',
    clearContinent: 'Clear continent',
    noMatch: 'No matching country or region',
    continents: { AS: 'Asia', EU: 'Europe', NA: 'North America', SA: 'South America', AF: 'Africa', OC: 'Oceania', AN: 'Antarctica' },
    notesTitle: 'Notes',
    noteUnknown: "The country comes from the visitor's IP. An IP no country can be found for (no GeoIP database on the node, private addresses) always passes, in either mode.",
    noteCityDb: "Provinces and cities need the GeoLite2-City database on the node. Without it the visitor's province and city cannot be told, so those entries never refuse anyone; in allow mode the rule then works at country level.",
    noteWhitelist: 'Addresses on the IP whitelist are exempt, so a partner inside a blocked region can still be let in.',
    noteOrder: 'Region block runs before forced verification and the CC, WAF and other rules; a blocked request goes no further.',
    saved: 'Region block saved',
    saveFailed: 'Failed to save region block',
  },
  cacheJobs: {
    targetsCount: '{n} target(s)',
    progress: '{done}/{total} nodes',
    historyCount: '{n} in all',
    section: 'Cache Purge',
    intro: 'Purging makes the nodes discard what they have cached, so the next request refetches from the origin. Prefetching pulls content to the nodes ahead of time so the first visitor already gets a hit. Jobs are sent to every node; one that is offline keeps its job queued and runs it when it returns.',
    kind: 'Type',
    sectionPurge: 'Cache Purge',
    sectionPrefetch: 'Cache Prefetch',
    prefetchIntro: 'Pull content to the nodes ahead of time so the first visitor already gets a hit. Useful before a release or a sale, to spread new assets out in advance.',
    modeNow: 'Run now',
    modeScheduled: 'Schedule',
    scheduledAt: 'Run at',
    scheduledHint: 'The job is sent to every node at this time. Up to 30 days ahead.',
    scheduledRequired: 'Pick a time',
    prefetchHistory: 'Prefetch history',
    purgeHistory: 'Purge history',
    status_scheduled: 'Scheduled',
    scheduledFor: 'scheduled for {t}',
    emptyPrefetch: 'No prefetch jobs yet',
    kindPurgeUrl: 'Purge URLs',
    kindPurgeDir: 'Purge directories',
    kindPurgeAll: 'Purge everything',
    kindPrefetch: 'Prefetch URLs',
    kind_purge_url: 'Purge URLs',
    kind_purge_dir: 'Purge dirs',
    kind_purge_all: 'Purge all',
    kind_prefetch: 'Prefetch',
    targets: 'Targets',
    targetsPlaceholder: 'One per line, e.g.\n/index.html\n/assets/app.js\nhttps://example.com/logo.png',
    targetsDirPlaceholder: 'One directory per line, e.g.\n/assets/\n/static/img/',
    targetsHelp: '{n} recognised, up to 1000 per job. Paths or full URLs; a URL must be on this site.',
    purgeAllWarning: 'This discards everything cached for this site on every node. Requests will go to the origin for a while afterwards and its load will rise noticeably — avoid doing this at peak.',
    submit: 'Submit',
    scheduledOk: 'Scheduled; it will be sent to the nodes at that time',
    submitted: 'Submitted; sending to the nodes',
    submitFailed: 'Could not submit this job',
    history: 'Job history',
    nodes: 'Nodes',
    failedN: '{n} failed',
    status_pending: 'Pending',
    status_running: 'Running',
    status_done: 'Done',
    status_failed: 'Partly failed',
    createdAt: 'Submitted',
    wholeSite: '(whole site)',
    empty: 'No jobs yet',
  },
  challenge: {
    title: 'Forced verification',
    desc: 'Every visitor must pass a human check before reaching the site. Those who pass are not asked again until the pass expires.',
    warn: 'This changes the first page every visitor sees. Check the search-engine and exempt-path settings below before enabling, or you risk losing search indexing or breaking API calls.',
    mode: 'Method',
    mode_wait: '5-second shield',
    mode_js: 'JS check',
    mode_click: 'Click to verify',
    mode_slider: 'Slide to verify',
    modeHint_wait: 'Shows "checking your browser" and passes on its own once the work finishes. No interaction.',
    modeHint_js: 'Same as the shield, with a visible progress bar.',
    modeHint_click: 'One button click. The work runs on page load; the click only confirms it.',
    modeHint_slider: 'The visitor drags a slider to the right.',
    ttl: 'Pass duration (seconds)',
    ttlHint: 'How long a passing visitor goes unchallenged. Default 1800 (30 minutes).',
    difficulty: 'Work factor',
    difficultyHint: 'Each step doubles the cost to a bot. Default 16, roughly tens of milliseconds in a modern browser. Below 12 offers little protection; above 20 is noticeably slow on low-end phones.',
    bypassSpiders: 'Allow search engines',
    bypassSpidersHint: 'Crawlers whose address reverse-resolves to the search engine skip verification; a forged user-agent still does not. Turning this off removes the site from search indexes, because crawlers do not run JavaScript.',
    exemptPaths: 'Exempt paths',
    exemptPathsHint: 'One path prefix per line; matching requests skip verification. APIs, payment callbacks and health checks belong here — they are not browsers and can never pass. Matched on whole segments: /api exempts /api/orders but not /apiary.',
    saved: 'Saved and published',
    saveFailed: 'Could not save',
  },
  ccProtection: {
    title: 'CC protection',
    level: 'Protection level',
    levels: { off: 'Off', loose: 'Low', normal: 'Medium', strict: 'High', attack: 'Under attack' },
    levelHint_off: 'No built-in protection; only the custom rules below apply.',
    levelHint_loose: 'A JS challenge for any IP over 600 requests in 10 seconds. Almost never hits real users; suits busy, API-heavy sites.',
    levelHint_normal: 'An IP over 300 requests in 10 seconds must pass a JS challenge for 5 minutes; 5 repeat offences ban it for 10 minutes.',
    levelHint_strict: 'An IP over 120 requests in 10 seconds must pass a JS challenge for 10 minutes; 3 repeat offences kernel-ban it for 30 minutes.',
    levelHint_attack: 'Every visitor gets a JS challenge; an IP over 60 requests in 10 seconds gets a slider challenge for 15 minutes; 2 repeat offences kernel-ban it for an hour.',
    attackWarn: 'Under attack mode challenges every visitor, which breaks APIs and clients that cannot run JavaScript. Turn it back down once the attack is over.',
    auto: 'Adaptive CC',
    autoHint_off: 'Never switches on by itself. Under a CC attack, set the protection level to Under attack by hand.',
    autoHint_loose: 'Challenges new visitors once requests reach 6× the usual while half the requests to the origin fail or slow right down. Suits sites whose traffic swings a lot.',
    autoHint_normal: 'Challenges new visitors once requests reach 4× the usual while the origin starts failing or slowing; switches off 5 minutes after requests are back to normal.',
    autoHint_strict: 'Challenges new visitors at 2.5× the usual requests and the first sign of origin trouble. Suits origins with little headroom.',
    autoCommon: 'Each node judges by the site\'s usual requests and origin speed. Visitors who already passed, exempt paths and search engines are never challenged.',
    autoMode: 'Verification',
    autoActiveTitle: 'Adaptive CC is on',
    autoActive: 'Since {since}, new visitors are being challenged because {reason} (nodes: {nodes}). Requests now: {rps} a second, against about {baseline} usually. It switches off by itself 5 minutes after requests are back to normal.',
    autoReason_failing: 'the origin was failing requests',
    autoReason_slow: 'the origin was answering slowly',
    autoMoot: 'With the level at Under attack, or forced challenges on, every new visitor is challenged already; adaptive CC adds nothing.',
    failBan: 'Ban on failed challenges',
    failBanDesc: 'An IP that fails the challenge this many times within the period is blacklisted: the node drops it in the kernel and records it in the ban log. Applies to forced challenges and to challenges from CC rules.',
    failMax: 'Failures',
    failMaxHint: 'Each challenge page not passed, and each wrong answer, counts once. Passing resets the count.',
    failWindow: 'Period (s)',
    failBanSec: 'Ban duration (s)',
    failBanSecHint: 'At most 30 days.',
    saved: 'CC protection saved',
    saveFailed: 'Failed to save CC protection',
  },
  domains: {
    packageCol: 'Package',
    switchPackage: 'Switch',
    switchTitle: 'Switch the package of {name}',
    switchExpires: 'valid until {time}',
    switchCurrent: 'current',
    switchNote: 'The site moves to the new package\'s traffic, request, bandwidth and connection quota at once. If the new package uses another line, the site gets a new CNAME: the old one keeps working, and the site moves to the new line once your DNS points at the new one.',
    switchDone: 'Package switched',
    switchCnameTitle: 'New CNAME',
    switchCnameChanged: 'The new CNAME for {name} is {new}. Change the CNAME record at your DNS provider to it to move to the new package\'s line. Until then the old one, {old}, keeps working and the site stays up.',
    suspended_plan_expired: 'Suspended: plan expired',
    suspended_traffic_exceeded: 'Suspended: traffic used up',
    suspended_requests_exceeded: 'Suspended: requests used up',
    suspendedTip: 'Visitors see the "Service suspended" page. Service resumes within a minute of renewing the plan.',
    title: 'Sites',
    add: 'New site',
    addModalTitle: 'New site',
    addPlaceholder: 'e.g. cdn.example.com',
    nameLabel: 'Primary domain',
    nameCol: 'Primary domain',
    statusCol: 'Status',
    sslCol: 'SSL',
    sslModeCol: 'SSL mode',
    proxyModeCol: 'Proxy mode',
    publishCol: 'Version',
    liveVersionCol: 'Live version',
    rules: 'Rules',
    publish: 'Publish',
    publishSuccess: 'Published',
    publishedTo: 'Published {version}',
    publishFailed: 'Publish failed',
    nameRequired: 'Domain name is required',
    packageLabel: 'Package',
    packagePlaceholder: 'Select a currently-active package',
    packageRequired: 'Please select a package',
    noActivePackageHint: 'You have no active package yet — purchase one before adding a site',
    requestSSL: 'Request certificate',
    createSuccess: 'Site created',
    createFailed: 'Failed to create site',
    statusDraft: 'Provisioning',
    statusPendingVerify: 'Pending verify',
    statusActive: 'Active',
    statusPaused: 'Paused',
    statusDisabled: 'Disabled',
    disable: 'Disable',
    enable: 'Enable',
    disableConfirmTitle: 'Disable site',
    disableConfirmContent: 'Disable "{name}"? Traffic for this site will no longer be proxied.',
    disableSuccess: 'Disabled',
    disableFailed: 'Disable failed',
    enableSuccess: 'Enabled',
    enableFailed: 'Enable failed',
    aliasDeleteConfirm: 'Remove {host}? The nodes will stop answering on it.',
    originDeleteConfirm: 'Remove origin {addr}?',
    originAddrRequired: 'Enter the origin address',
    deleteSite: 'Delete site',
    changePackageAction: 'Change package',
    listTitle: 'All sites',
    listCount: '{n} in all',
    listFiltered: '{n} shown',
    searchPlaceholder: 'Search by domain or CNAME',
    filterAllPackages: 'All packages',
    filterAllStatus: 'All statuses',
    filterAllSsl: 'All SSL modes',
    filterSuspended: 'Suspended',
    resetFilters: 'Reset',
    statTotal: 'Sites',
    statToday: 'Today +{n}',
    statActive: 'Active',
    statDraft: 'Provisioning',
    statDisabled: 'Disabled',
    statSuspended: '{n} suspended',
    statDraftNote: 'waiting on your CNAME',
    siteCol: 'Site',
    createdAtCol: 'Created',
    updatedAtCol: 'Last changed',
    more: 'More',
    ssl_off: 'Off',
    ssl_flexible: 'Flexible',
    ssl_full: 'Full',
    ssl_strict: 'Full (strict)',
    ssl_force_https: 'Force HTTPS',
    sslHint_off: 'Visitors use plain HTTP only; the nodes do not listen on HTTPS.',
    sslHint_flexible: 'HTTPS from the visitor to the node, HTTP from the node to the origin. For an origin without a certificate.',
    sslHint_full: 'HTTPS end to end; the origin\'s certificate is not checked, so a self-signed one works.',
    sslHint_strict: 'HTTPS end to end, and the origin\'s certificate must be valid and match the origin Host.',
    sslHint_force_https: 'HTTPS end to end; plain HTTP requests are redirected with a 301. The origin\'s certificate is not checked.',
    proxy_proxy: 'Proxied',
    proxy_dns_only: 'DNS only',
    proxyHint_proxy: 'Traffic goes through the nodes: caching, protection rules and certificates apply there.',
    proxyHint_dns_only: 'Only the DNS record is kept; traffic reaches the origin directly and nothing on the nodes applies.',
    realIpHeaderHint: 'The nodes put the visitor\'s real IP in this header for the origin, which reads the client address from it.',
    hostCol: 'Hostname',
    hostTypeCol: 'Type',
    primaryTag: 'Primary',
    aliasTag: 'Alias',
    aliasCount: '{n} hostname(s)',
    hostVerifyCol: 'DNS check',
    hostVerified: 'Verified',
    hostPending: 'Pending',
    hostForced: 'Forced',
    hostForcedTip: 'An admin skipped the CNAME check',
    hostPendingTip: 'Point the CNAME of {host} at {target}. It is checked automatically; once changed, Verify checks it now.',
    hostPrimaryPendingTip: 'The main domain is verified under CNAME setup in Overview.',
    hostNoTargetTip: 'The site has no CNAME address yet, so it cannot be verified.',
    aliasVerify: 'Verify',
    aliasVerified: '{host} verified; the nodes now serve it',
    aliasVerifyMismatch: 'The CNAME does not point here yet',
    aliasVerifyFailed: 'Verification failed',
    addOrigin: 'Add origin',
    addOriginTitle: 'Add origin',
    originScheme: 'Scheme',
    originPort: 'Port',
    originWeight: 'Weight',
    originRole: 'Role',
    originPrimary: 'Primary',
    originBackup: 'Backup',
    originBackupLabel: 'Use as a backup origin',
    originBackupHint: 'Used only when every primary origin is unreachable.',
    originWeightHint: 'Requests are shared between primary origins by weight.',
    originSni: 'SNI',
    originAddrPlaceholder: 'IP or hostname, e.g. 10.0.0.8 or origin.example.com',
    originStatus_active: 'Enabled',
    originStatus_disabled: 'Disabled',
    originCount: '{n} origin(s)',
    basicSettings: 'Basic settings',
    facts: 'Details',
    noPackage: 'None',
    navPerformance: 'Performance',
    navAnalytics: 'Analytics & logs',
    siteId: 'Site ID',
    sectionTitle_overview: 'Overview',
    sectionDesc_overview: 'How the site behaves: HTTPS redirect, SSL mode, protocol switches. Changes publish to every node automatically.',
    sectionTitle_domains: 'Domains',
    sectionDesc_domains: 'Several hostnames can share one configuration, including its origins, certificate and every protection rule.',
    'sectionTitle_cache-prefetch': 'Cache Prefetch',
    'sectionDesc_cache-prefetch': 'Pull content to the nodes ahead of time so the first visitor already gets a hit. Can run now or at a chosen time.',
    'sectionTitle_cache-jobs': 'Cache Purge',
    'sectionDesc_cache-jobs': 'Make the nodes discard what they have cached so the next request refetches it. Jobs are sent to every node.',
    sectionTitle_origins: 'Origins',
    sectionDesc_origins: 'Where requests are forwarded. Multiple origins are weighted; backups are used only when the primaries are unreachable.',
    sectionTitle_certificates: 'Certificates',
    sectionDesc_certificates: 'Issue or upload the HTTPS certificate. Automatic issuance is available from Let\'s Encrypt and ZeroSSL.',
    navBasics: 'Basics',
    navProtection: 'Protection',
    manage: 'Manage',
    detailTitle: 'Site detail',
    backToList: '← Back to sites',
    syncStatusLabel: 'Sync status',
    syncNow: 'Sync now',
    syncState_never: 'Not synced',
    syncState_pending: 'Queued',
    syncState_syncing: 'Syncing',
    syncState_synced: 'Synced',
    syncState_failed: 'Sync failed',
    forceHttpsLabel: 'Force HTTPS',
    forceHttpsHint: 'Redirects plain HTTP requests to HTTPS with a 301. Certificate validation requests are exempt.',
    sectionOverview: 'Overview',
    sectionDomains: 'Domains',
    upstreamLimitTitle: 'Origin concurrency limit',
    upstreamLimitHelp: 'The most requests one node will have in flight to the origin at once. Anything beyond it is refused with 503 at the edge instead of being forwarded. This is the one protection that does not depend on a filter being right — however much traffic the edge absorbs, the origin never sees more than this at a time. 0 means no limit.',
    upstreamLimitOff: '0 = no limit',
    upstreamLimitOn: 'Currently capped at {n} concurrent origin requests per node; the rest get a 503 at the edge.',
    upstreamLimitOffHint: 'No limit: the edge forwards everything it receives, so origin load tracks attack volume. Set it from the origin\'s measured capacity.',
    sectionOrigins: 'Origins',
    sectionCertificates: 'Certificates',
    aliases: 'Aliases',
    aliasesHint: 'Every domain on a site shares its origin and rules. A new one is served once its CNAME points here and passes the check.',
    aliasHostnameCol: 'Domain',
    aliasesEmpty: 'No aliases yet',
    aliasesPlaceholder: 'e.g. www.example.com',
    aliasAdd: 'Add',
    aliasAddSuccess: 'Added',
    aliasAddFailed: 'Failed to add',
    cnameTargetCol: 'CNAME Target',
    origins: 'Origins',
    protocolsLabel: 'Protocols',
    realIpHeaderLabel: 'Real IP header',
    originAddr: 'Origin address',
    originHostHeader: 'Origin Host',
    originHostHeaderPlaceholder: 'Origin Host (empty: the site name)',
    originSniPlaceholder: 'SNI (empty: same as Origin Host)',
    originTlsHint: "For an HTTPS origin the certificate is checked only when the SSL mode is strict; otherwise a self-signed or IP certificate works. Origin Host is the Host header the origin receives — set it to the origin's own name for a virtual host or an object store.",
    originsEmpty: 'No origins yet',
    originAddSuccess: 'Origin added',
    originAddFailed: 'Failed to add origin',
    publishedToPlain: 'Published {version}',
    saveFailed: 'Save failed',
    saveSuccess: 'Saved',
    deleteModalTitle: 'Delete site',
    deleteModalContent: 'Delete site "{name}"? This cannot be undone.',
    deleteSuccess: 'Deleted',
    deleteFailed: 'Delete failed',
    loadFailed: 'Failed to load sites',
    emptyHint: 'No sites yet',
    sectionAnalytics: 'Traffic',
    sectionTitle_analytics: 'Traffic analytics',
    sectionDesc_analytics: 'Bandwidth, requests, traffic, blocks and cache hits of the site and every name it answers on, with the top lists.',
    notInPackage: 'Not in package',
    hostnameQuota: 'Package hostnames {used} / {max}',
    apexQuota: 'Apex domains {used} / {max}',
    notInPackageTip: 'Your package “{package}” does not include {feature}: it cannot be turned on, and if on it does not apply (it can be turned off).',
    hostnamesFull: 'Your package allows {max} hostnames, all in use',
    pkgSites: 'Sites {used}/{max}',
    pkgFull: 'No sites left',
  },
  // What keeps a site from being served properly (SiteReadiness.vue,
  // utils/readiness.ts).
  readiness: {
    noOrigin: 'No origin yet: every request is answered with a 502 (Bad gateway).',
    noCert: 'No usable certificate for {hosts}: HTTPS fails outright, and browsers report an unsupported protocol.',
    certMismatch: 'Certificate does not match {hosts}: browsers warn that the connection is not secure.',
    addOrigin: 'Add origin',
    getCert: 'Get certificate',
    changeCert: 'Change certificate',
    nameSep: ', ',
    andMore: '{names} and {n} more',
    shortNoOrigin: 'No origin',
    shortNoCert: 'No certificate',
    shortMismatch: 'Certificate mismatch',
    aliasPending: 'CNAME not verified yet for {hosts}: not served until it is.',
    verifyAlias: 'Verify',
    shortAliasPending: 'Alias pending',
    listHint: 'This site cannot be served properly yet',
  },
  domainDetail: {
    back: '← Back',
    overview: 'Overview',
    rules: 'Rules',
    origins: 'Origins',
    certificates: 'Certificates',
    requests: 'Requests',
    bandwidth: 'Bandwidth',
    errors5xx: '5xx',
    cacheHitRate: 'Cache hit rate',
    requestsPerMin: 'Requests per minute',
    topUrls: 'Top URLs (24h)',
    legendRequests: 'requests',
    legendCacheHits: 'cache hits',
    legend5xx: '5xx',
    noDataInRange: 'No data in this range',
    emptyTopUrls: 'No top URLs yet',
    range1h: 'Last 1 h',
    range24h: 'Last 24 h',
    range7d: 'Last 7 d',
    domainNotFound: 'Domain not found',
    loadFailed: 'Load failed',
    cnameCardTitle: 'CNAME setup',
    cnameInstructions: 'Add this record at your DNS provider and visitors will reach the site through the edge:',
    cnameGenerating: 'Generating…',
    cnameGeneratingHint: 'Your plan has no CNAME domain configured yet — contact your admin',
    cnameStatusActive: 'Active',
    cnameStatusPending: 'Pending verification',
    cnameVerifyButton: 'Verify',
    cnameVerifyFailed: 'Verification failed',
    cnameVerifySuccess: 'Verified — domain is now active',
    cnameVerifyMismatch: 'No matching CNAME found yet — make sure it\'s configured and give it time to propagate',
    aliasesCardTitle: 'Site domains',
    aliasesHint: 'Every domain on a site shares its origin and rules. A new one is served once its CNAME points here and passes the check.',
    aliasesPlaceholder: 'e.g. www.example.com',
    aliasAdd: 'Add',
    aliasAddSuccess: 'Added',
    aliasAddFailed: 'Failed to add',
    uriCol: 'URI',
    bytesCol: 'Bytes',
    avgRt: 'Avg RT',
    p95Rt: 'P95 RT',
    addOrigin: 'Add origin',
    issueCert: 'Issue certificate',
    acmeEmail: 'Email',
    acmeStaging: 'ACME staging',
    acmeAgree: 'I agree to Let’s Encrypt TOS',
    recordType: 'Type',
    recordHost: 'Host',
    recordValue: 'Value',
    cnameAliasHint: 'Aliases need the same CNAME and are served once it is verified; see each one under Domains.',
  },
  rules: {
    customRules: 'Custom rules',
    ruleCount: '{n} in all',
    kindDesc_acl: 'Allow or block by IP, network, region, ASN, user-agent or TLS fingerprint. An explicit allow wins over every other rule.',
    kindDesc_geo: 'Block, challenge or allow visitors by their country or region. Runs before the other protection rules.',
    kindDesc_cc: 'Rate limiting. When one source exceeds the threshold within the window it can be throttled, blocked, challenged or banned.',
    kindDesc_waf: 'Matches SQL injection, XSS and command execution signatures. Hits accumulate a score and only block past the site threshold, so one false match cannot take a site down.',
    kindDesc_bot: 'Catches crawlers impersonating search engines, and known scanners. Genuine spiders pass via reverse-DNS verification.',
    kindDesc_cache: 'Match by suffix, directory, path or status code, and set how long the nodes cache it and whether to force caching.',
    backToDomain: 'Back to site',
    title: 'Rules',
    backToDomains: '← Back to sites',
    pageTitle: 'Site #{id} — rules',
    selectKind: 'Select rule kind',
    kindAcl: 'Access control',
    kindGeo: 'Region block',
    kindWaf: 'WAF',
    kindCc: 'CC protection',
    kindBot: 'Bot',
    kindCache: 'Cache Rules',
    add: 'New rule',
    publish: 'Publish',
    priority: 'Priority',
    priorityHint: 'Lower numbers run first.',
    enabledHint: 'This rule is active and will be applied on the next publish.',
    disabledHint: 'This rule is saved but will not be applied.',
    action: 'Action',
    typeCol: 'Type',
    categoryCol: 'Category',
    statusCol: 'Status',
    addModalTitle: 'New {kind} rule',
    editModalTitle: 'Edit {kind} rule',
    deleteModalTitle: 'Delete rule?',
    deleteModalContent: 'Rule #{id} ({name}) will be removed. Republish the domain to apply on edge.',
    deleteConfirm: 'Delete this rule?',
    deleteSuccess: 'Deleted',
    deleteFailed: 'Delete failed',
    addSuccess: 'Rule added',
    addFailed: 'Failed to add',
    updateSuccess: 'Rule updated',
    validationFailed: 'Please fix the highlighted fields',
    loadFailed: 'Failed to load rules',
    invalidJson: 'Invalid JSON',
    publishedTo: 'Published {version} → {n} node(s)',
    publishedToPlain: 'Published {version}',
    publishFailed: 'Publish failed',
    emptyHint: 'No rules yet — click New rule to create one.',
    actions: {
      allow: 'Allow',
      log: 'Log only',
      rate_limit429: 'Rate limit (429)',
      challenge_js: 'JS challenge',
      challenge_slider: 'Slider challenge',
      challenge_click: 'Click challenge',
      deny403: 'Block (403)',
      deny444: 'Block (444, silent)',
      ban: 'Ban IP',
      deny: 'Deny (custom status)',
      ban_kernel: 'Kernel ban',
    },
    acl: {
      matchCol: 'Match',
      namePlaceholder: 'e.g. block-known-scanners',
      types: {
        ip: 'IP address',
        cidr: 'IP range (CIDR)',
        geo: 'Country',
        asn: 'ASN',
        ua: 'User-Agent',
        referer: 'Referer',
        header: 'Request header',
        ja4: 'JA4 fingerprint',
        ja3: 'JA3 fingerprint',
      },
      matchValues: 'Values',
      matchCountries: 'Countries',
      matchAsn: 'ASN numbers',
      matchContains: 'Contains (case-insensitive)',
      headerName: 'Header name',
      placeholderIp: 'e.g. 203.0.113.7 — press Enter to add',
      placeholderCidr: 'e.g. 203.0.113.0/24 — press Enter to add',
      placeholderCountry: 'e.g. CN, US — press Enter to add',
      placeholderAsn: 'e.g. 13335 — press Enter to add',
      placeholderSubstring: 'e.g. curl/ — press Enter to add',
      placeholderJa4: 'e.g. t13d1516h2_8daaf6152771_02713d6af862 — press Enter to add',
      placeholderJa3: 'e.g. 773906b0efdefa24a7f2b8eb6985bf37 — press Enter to add',
      fingerprintHint: 'The TLS client fingerprint, matched exactly as the access log shows it (case aside). Plain-HTTP requests have none and never match.',
    },
    cc: {
      namePlaceholder: 'e.g. login-bruteforce',
      matchTitle: 'Match',
      methods: 'Methods',
      methodsPlaceholder: 'Any',
      path: 'Path',
      pathPlaceholder: 'Leave empty for every path, e.g. /api/login',
      pathModes: { prefix: 'Starts with', exact: 'Equals', contains: 'Contains' },
      countStatus: 'Count only responses with status',
      countStatusPlaceholder: 'e.g. 401,403 or 5xx; empty counts every request',
      countStatusHint: 'Counts only requests answered with these statuses. Use 401,403 against credential stuffing, 404 against path scanning.',
      countTitle: 'Counting',
      dimensions: 'Count by',
      dimensionsHint: 'Requests are grouped by these fields before counting, up to 3. IP + path counts each visitor separately per endpoint; a header gives each API key its own limit.',
      fingerprintHint: 'Every copy of one browser version has the same TLS fingerprint, so counting by fingerprint alone counts all of them as one client. Use it for scripts and tools with fingerprints of their own, or together with the path. Plain-HTTP requests have no fingerprint and are counted together. Chrome changes its JA3 on every connection; prefer JA4.',
      dims: {
        ip: 'Client IP',
        uri: 'Request path',
        ua: 'User-Agent',
        host: 'Hostname',
        ja4: 'TLS fingerprint (JA4)',
        ja3: 'TLS fingerprint (JA3)',
        country: 'Country/region',
        header: 'Request header',
        cookie: 'Cookie',
      },
      dimNamePlaceholder_header: 'Header name, e.g. X-Api-Key',
      dimNamePlaceholder_cookie: 'Cookie name, e.g. session_id',
      addDimension: '+ Add dimension',
      windowSeconds: 'Period (s)',
      threshold: 'Requests',
      thresholdCol: 'Limit / period',
      matchCol: 'Match',
      anyRequest: 'All requests',
      statusOnly: 'only {status}',
      actionTitle: 'Action',
      statusCode: 'Response status',
      mitigation: 'Mitigation',
      mitigations: { throttle: 'Throttle', penalty: 'Penalty' },
      mitigationHint_throttle: 'Only the requests over the limit get the action; traffic recovers as soon as the rate drops.',
      mitigationHint_penalty: 'Once tripped, every request from the source gets the action for the whole duration.',
      ttlSeconds: 'Duration (s)',
      ttlHint: 'How long a passed challenge is remembered, or how long the ban or penalty lasts.',
      escalationTitle: 'Escalation (optional)',
      addTier: '+ Add tier',
      escalationHint: 'A source that keeps tripping the rule gets a harsher action after this many violations, up to 5 tiers.',
      escalationEmpty: 'None: every violation gets the action above.',
      afterViolations: 'After',
      thenAction: 'violations, escalate to',
      forSeconds: 'for (s)',
    },
    waf: {
      namePlaceholder: 'e.g. block-sqlmap-signature',
      categories: {
        sqli: 'SQL injection',
        xss: 'Cross-site scripting',
        rce: 'Remote code execution',
        traversal: 'Path traversal',
        scanner: 'Scanner / recon',
        webshell: 'Webshell',
        custom: 'Custom',
      },
      targets: 'Inspect',
      targetsCol: 'Inspects',
      targetsPlaceholder: 'Select one or more',
      targetLabels: { uri: 'URL path', args: 'Query string', cookie: 'Cookie header', method: 'HTTP method' },
      alsoInspectHeader: 'Also inspect a specific request header',
      pattern: 'Pattern (regex)',
      patternHint: 'Matched case-insensitively against every selected target.',
      score: 'Score',
      scoreHint: 'Added to the domain\'s running total when this rule matches; the request is blocked once the total crosses the WAF threshold.',
    },
    bot: {
      namePlaceholder: 'e.g. block-scraping-tool',
      matchMode: 'Match mode',
      modeSimple: 'Contains (simple)',
      modeAdvanced: 'Regex (advanced)',
      uaContains: 'User-Agent contains',
      uaContainsHint: 'Case-insensitive substring match — no regex syntax needed.',
      uaRegex: 'User-Agent regex',
      uaRegexHint: 'Full regular expression, matched case-insensitively.',
      patternCol: 'Pattern',
    },
    cache: {
      matchCol: 'Match',
      matchSuffix: 'File extensions',
      matchDir: 'Path prefixes',
      matchUri: 'Exact paths',
      matchStatus: 'Status codes',
      ttlSeconds: 'TTL (seconds)',
      forceCache: 'Force cache (ignore origin cache headers)',
      bypassCookies: 'Bypass on cookie names',
      varyQuery: 'Vary cache key by query params',
    },
    notInPackage_waf: 'Your package “{package}” does not include WAF: no WAF rule runs on this site, the built-in ones included, and none can be added or changed. On a package with WAF the rules below take effect again.',
    notInPackageOps_waf: 'This site’s package “{package}” does not include WAF: the nodes run none of its WAF rules, the built-in ones included. The rules below are kept and take effect when the site moves to a package with WAF.',
    notInPackage_cc: 'Your package “{package}” does not include custom CC rules: the protection level and forced verification above still apply; the rules below do not, and none can be added or changed.',
    notInPackageOps_cc: 'This site’s package “{package}” does not include custom CC rules: the rules below are kept but do not apply; the protection level and forced verification still do.',
    notInPackageShort: 'Not included in your package',
    quotaUsed: 'Rules {used} / {max}',
    quotaFull: 'Your package allows {max} rules per site, all in use',
  },
  errors: {
    network: 'Network error',
    unknown: 'Unknown error',
    notFound: 'Not found',
    forbidden: 'Forbidden',
  },
  certificates: {
    title: 'Certificates',
    sectionTitle: 'Certificate management',
    domain: 'Domain',
    typeUpload: 'Upload',
    typeAcme: 'ACME',
    commonNameCol: 'Common name',
    issuerCol: 'Issuer',
    notBeforeCol: 'Not before',
    notAfterCol: 'Not after',
    statusCol: 'Status',
    fingerprintCol: 'Fingerprint',
    expiringDays: 'Expires in {n} day(s)',
    expired: 'Expired',
    issueAcme: 'Request via ACME',
    refresh: 'Refresh',
    empty: 'No certificates for this domain yet',
    aboutExpire: 'Expiring soon',
    stateCancelled: 'Cancelled',
    attachTitle: 'Use an existing certificate',
    issueCancelled: 'Cancelled',
    issueRetryNow: 'Retry now',
    uploadMoved: 'Upload certificates on the certificates page:',
    attachSuccess: 'Applied to this site',
    attachPlaceholder: 'Pick an existing certificate',
    attach: 'Use this certificate',
    pemRequired: 'Both the certificate and the key are required',
    siteRequired: 'Choose a site',
    sitePlaceholder: 'Choose a site',
    siteHint: 'The certificate is bound to this site and pushed to the nodes. It can be re-pointed at another site from that site\'s page.',
    siteLabel: 'Apply to site',
    noSite: 'unassigned',
    siteCol: 'Site',
    emailHint: 'Used to register with the CA and receive expiry notices. Requesting a certificate accepts that CA\'s terms of service.',
    stateNextRun: 'Next retry: {t}',
    stateFailed: 'Issuance failed',
    stateSuccess: 'Certificate issued',
    statePending: 'Issuing… (attempt {n}/{max})',
    issueRetry: 'Request again',
    issueQueueHint: 'Issuance runs in the background queue; you can leave this page. Failures are shown above, and an operator can read every attempt in Task Queue → System tasks.',
    issueQueued: 'Queued — the result appears above',
    emailLabel: 'Email',
    emailRequired: 'Email is required',
    caLabel: 'Certificate authority',
    issueFailed: 'Failed to issue certificate',
    uploadCert: 'Upload certificate',
    uploadSuccess: 'Certificate uploaded',
    uploadFailed: 'Failed to upload certificate',
    statTotal: 'Certificates',
    statValid: 'Valid',
    statExpiring: 'Expiring',
    statExpiringNote: 'within {n} days',
    statExpired: 'Expired',
    statRevoked: '{n} revoked',
    listTitle: 'All certificates',
    listCount: '{n} in all',
    listFiltered: '{n} shown',
    searchPlaceholder: 'Search names or issuer',
    filterAllSites: 'All sites',
    filterAllTypes: 'All types',
    filterAllStatus: 'All statuses',
    state_valid: 'Valid',
    state_expiring: 'Expiring',
    state_expired: 'Expired',
    state_revoked: 'Revoked',
    certCol: 'Certificate',
    moreNames: '+{n} more',
    typeCol: 'Type',
    expiresCol: 'Expires',
    emptyHint: 'No certificates yet. Get a free one on a site\'s SSL pane, or upload one here.',
    certPem: 'Certificate (PEM)',
    certPemHint: 'The server certificate first, then the intermediates: the whole chain.',
    keyPem: 'Private key (PEM)',
    keyPemHint: 'Stored encrypted; used only for the HTTPS handshake on the nodes.',
    uploadsNotInPackage: 'None of your packages includes uploaded certificates; free certificates are available on each site.',
  },
  billing: {
    title: 'Add funds',
    statBalance: 'Balance',
    statPaid: 'Total top-ups',
    statPending: 'Awaiting payment',
    unitYuan: 'CNY',
    unitOrders: 'orders',
    closed: 'Online top-up is not available yet. Contact the administrator to add funds',
    amount: 'Amount',
    otherAmount: 'Other',
    customPlaceholder: 'Enter an amount',
    customRequired: 'Enter the amount to top up',
    outOfRange: 'Outside the amount allowed per top-up',
    range: '¥{min} – ¥{max} per top-up',
    method: 'Pay with',
    choice_alipay: 'Alipay',
    choiceSub_alipay: 'Continue to Alipay to pay',
    choice_qr: 'Alipay QR code',
    choiceSub_qr: 'Scan with the Alipay app',
    due: 'Amount due',
    pay: 'Top up now',
    intro: 'Funds are added to your balance as soon as the payment goes through. If your balance has not changed after paying, click "Check status" in the top-up history, or contact us with the order number.',
    listCount: '{n} in total',
    noOrders: 'No top-ups yet',
    noLedger: 'No transactions yet',
    state_pending: 'Unpaid',
    state_paid: 'Paid',
    state_closed: 'Closed',
    state_expired: 'Expired',
    method_page: 'Alipay · Web',
    method_wap: 'Alipay · Mobile',
    method_qr: 'Alipay · QR code',
    ask: 'Check status',
    askPaid: 'Payment confirmed. ¥{amount} has been added',
    askUnpaid: 'Alipay has not received payment for this order yet',
    balanceAfter: 'Balance ¥{amount}',
    colOrderNo: 'Order number',
    colAmount: 'Amount',
    colMethod: 'Payment method',
    colCreated: 'Created',
    colPaid: 'Paid',
    colTime: 'Time',
    colKind: 'Type',
    colChange: 'Amount',
    colBalance: 'Balance',
    colNote: 'Description',
    qrTitle: 'Scan to pay',
    qrHint: 'Open the Alipay app and scan to pay',
    qrLeft: 'Time left',
    qrOpen: 'Open Alipay to pay',
    paidTitle: 'Top-up successful',
    paidSub: '¥{amount} has been added to your balance',
    done: 'Done',
    close: 'Close',
    expiredTitle: 'Order expired',
    expiredSub: 'The time to pay for this order has run out. Start a new top-up',
    returnTitle: 'Payment result',
    checking: 'Confirming the payment with Alipay…',
    closedTitle: 'Order closed',
    closedSub: 'This order was not paid. Start a new top-up',
    unconfirmedTitle: 'Payment not confirmed yet',
    unconfirmedSub: 'If you have paid, your balance is usually updated within a few minutes. You can also check the status later in the top-up history',
    checkAgain: 'Check again',
    statRemits: 'Transfers to confirm',
    tabOnline: 'Online',
    tabOrders: 'History',
    tabRemit: 'Bank transfer',
    tabRemits: 'Transfers',
    remitClosed: 'Bank transfers are not available. Contact the administrator to pay by transfer',
    remitTo: 'Pay to',
    acctName: 'Name',
    acctBank: 'Bank',
    acctNo: 'Account',
    acctNote: 'Note',
    remitAmount: 'Amount',
    remitAmountPlaceholder: 'As on the bank receipt, at least ¥{min}',
    remitAmountRequired: 'Enter the amount transferred',
    remitAmountMin: 'The amount must be at least ¥{min}',
    payer: 'Paid by',
    payerPlaceholder: 'The account holder, as on the bank receipt',
    payerAccount: 'From account',
    optional: 'Optional',
    remitDate: 'Transfer date',
    reference: 'Reference',
    referencePlaceholder: 'Optional, the transaction number on the receipt',
    receipt: 'Receipt',
    receiptHint: 'A screenshot or PDF of the bank receipt, up to 5 MB',
    receiptTooLarge: 'The receipt must be 5 MB or smaller',
    remitNote: 'Note',
    remitNotePlaceholder: 'Optional, e.g. what the payment is for',
    remitSubmit: 'Submit transfer',
    remitIntro: 'Transfer the money to one of the accounts above through online banking or at a branch, then upload the bank receipt here. Once our finance team sees it arrive, the amount is added to your balance, usually within one working day.',
    remitted: 'Transfer submitted. Waiting for finance to confirm it',
    noRemits: 'No transfers yet',
    rejectedFor: 'Rejected: {note}',
    viewReceipt: 'View receipt',
    cancel: 'Cancel',
    cancelRemitTitle: 'Cancel this transfer?',
    cancelRemitContent: 'Finance will no longer look for this ¥{amount} transfer. If you have sent the money, submit the transfer again.',
    cancelled: 'Cancelled',
    colRemitNo: 'Transfer number',
    colRemitTo: 'Paid to',
    colRemitDate: 'Transfer date',
    colSubmitted: 'Submitted',
  },
  // 财务管理: 收支明细, 订单管理, 提现, 发票, 退款, and buying a package.
  ledger: {
    kind_realname: 'ID verification',
    kind_realname_back: 'ID verification refund',
    title: 'Transactions',
    statBalance: 'Balance',
    statPaid: 'Total top-ups',
    statSpent: 'Total spent',
    statOut: 'Withdrawn & refunded',
    listTitle: 'All transactions',
    allKinds: 'All types',
    group_topup: 'Top-ups',
    group_purchase: 'Purchases',
    group_refund: 'Refunds',
    group_withdraw: 'Withdrawals',
    group_adjust: 'Adjustments',
    clear: 'Clear filters',
    noMatch: 'No transactions match',
    colDoc: 'Reference',
    kind_topup: 'Online top-up',
    kind_remittance: 'Bank transfer',
    kind_adjust: 'Adjustment',
    kind_purchase: 'Package purchase',
    kind_order_refund: 'Package refund',
    kind_withdraw: 'Withdrawal',
    kind_withdraw_back: 'Withdrawal returned',
    kind_refund: 'Top-up refund',
    kind_refund_back: 'Refund returned',
    desc_topup: 'Alipay top-up',
    desc_remittance: 'Bank transfer, confirmed by finance',
    desc_purchase: 'Package "{name}"',
    desc_order_refund: 'Package "{name}" refunded to the balance',
    desc_withdraw: 'Withdrawal to Alipay, taken from the balance when applied for',
    desc_withdraw_back: 'Withdrawal not completed, returned to the balance',
    desc_refund: 'Refund to the paying Alipay account, taken from the balance when requested',
    desc_refund_back: 'Refund not completed, returned to the balance',
  },
  orders: {
    title: 'Orders',
    statOrders: 'Package orders',
    statSubs: 'Active packages',
    listTitle: 'Package orders',
    count: '{n} in total',
    buy: 'Buy a package',
    empty: 'No package orders yet',
    colOrderNo: 'Order number',
    colPackage: 'Package',
    colPeriod: 'Term',
    colAmount: 'Amount',
    colSpan: 'Service period',
    colCreated: 'Ordered',
    kind_buy: 'New',
    kind_renew: 'Renewal',
    state_paid: 'Paid',
    state_partly_refunded: 'Part refunded',
    state_refunded: 'Refunded',
    refunded: '¥{amount} refunded',
    askRefund: 'Refund',
  },
  withdraw: {
    title: 'Withdrawals',
    statCashable: 'Available to withdraw',
    statWithdrawn: 'Total withdrawn',
    statPending: 'In progress',
    statAccounts: 'Payout accounts',
    unitAccounts: 'accounts',
    tabApply: 'Withdraw',
    tabAccounts: 'Accounts',
    tabRecords: 'History',
    closed: 'Withdrawals are not available. Contact the administrator to withdraw',
    notReady: 'Withdrawals are not ready yet, so you cannot apply right now. Try again later or contact the administrator',
    cashable: 'Available',
    invoicedNote: 'The ¥{amount} invoiced cannot be withdrawn',
    toAccount: 'Withdraw to',
    accountRequired: 'Choose an account to withdraw to',
    noAccountYet: 'Add a payout account first',
    addAccount: 'Add payout account',
    amount: 'Amount',
    amountPlaceholder: 'At least ¥{min}',
    amountRequired: 'Enter the amount to withdraw',
    outOfRange: 'Outside the amount allowed per withdrawal',
    takeAll: 'Withdraw all',
    range: '¥{min} – ¥{max} per withdrawal',
    overCashable: 'You can withdraw at most ¥{max}',
    fee: 'Fee',
    feeFree: 'No fee',
    feeRate: '{rate}% of the amount',
    feeFixed: '¥{min} per withdrawal',
    feeRateMin: '{rate}% of the amount, at least ¥{min}',
    net: 'You receive',
    submit: 'Withdraw',
    applied: 'Withdrawal submitted for review',
    intro: 'The amount leaves your balance as soon as you apply. Once approved, it is transferred to your Alipay account, usually within 1–3 working days. If the withdrawal is rejected or cancelled, the amount returns to your balance. Invoiced money cannot be withdrawn.',
    accountsNote: '{n} of {max} added',
    noAccounts: 'No payout accounts yet',
    accountsIntro: 'Withdrawals go only to verified Alipay accounts, and the real name must match the one verified on Alipay or the transfer fails. Deleting an account does not affect withdrawals already submitted.',
    colRealName: 'Real name',
    colAccount: 'Alipay account',
    colLabel: 'Label',
    colAdded: 'Added',
    deleteTitle: 'Delete this payout account?',
    deleteContent: 'You will no longer be able to withdraw to {account}. Withdrawals already submitted are not affected.',
    accountAdded: 'Payout account added',
    realName: 'Real name',
    realNamePlaceholder: 'The name verified on Alipay',
    realNameHelp: 'Must match the name verified on Alipay, or the transfer fails',
    alipayAccount: 'Alipay account',
    alipayAccountPlaceholder: 'Email or mobile number',
    accountLabel: 'Label',
    accountLabelPlaceholder: 'Optional, e.g. "Company"',
    noRecords: 'No withdrawals yet',
    colNo: 'Withdrawal number',
    colAmount: 'Amount',
    colFee: 'Fee',
    colNet: 'Received',
    colTo: 'Paid to',
    colApplied: 'Applied',
    feeNet: 'Fee ¥{fee}, you receive ¥{net}',
    rejectedFor: 'Rejected: {note}',
    failedFor: 'The Alipay transfer did not go through: {reason}. We are looking into it',
    cancel: 'Cancel',
    cancelTitle: 'Cancel this withdrawal?',
    cancelContent: '¥{amount} will return to your balance.',
    cancelled: 'Cancelled. The amount has returned to your balance',
  },
  invoice: {
    title: 'Invoices',
    statOpen: 'Available to invoice',
    statDone: 'Total invoiced',
    statPending: 'Awaiting issue',
    statTitles: 'Invoice titles',
    unitInvoices: 'invoices',
    unitTitles: 'titles',
    tabApply: 'Request',
    tabRecords: 'History',
    tabProfiles: 'Titles',
    closed: 'Invoices cannot be requested online. Contact the administrator for an invoice',
    open: 'Available',
    openNote: 'Invoiced as paid: top-ups and transfers, less what was refunded, withdrawn or invoiced already',
    type: 'Type',
    typeLong_normal: 'General VAT invoice',
    typeLong_special: 'Special VAT invoice',
    type_normal: 'General',
    type_special: 'Special',
    medium: 'Delivery',
    medium_electronic: 'Electronic',
    medium_paper: 'Paper',
    header: 'Invoice title',
    headerPlaceholder: 'Choose an invoice title',
    headerCompany: 'Full company name, as registered',
    headerPersonal: 'Your name',
    kind: 'Title type',
    kind_company: 'Company',
    kind_personal: 'Personal',
    taxNoShort: 'Tax ID',
    personalLine: 'Personal title',
    addProfile: 'Add invoice title',
    editProfile: 'Edit invoice title',
    amount: 'Amount',
    amountPlaceholder: 'At least ¥{min}',
    amountRequired: 'Enter the amount to invoice',
    amountHint: 'At least ¥{min} per invoice',
    takeAll: 'Invoice all',
    submit: 'Submit request',
    applied: 'Invoice requested',
    intro: 'Invoices are issued for the amount paid, and invoiced money can no longer be withdrawn or refunded. Electronic invoices can be downloaded from the invoice history once issued; paper invoices go by courier, with the tracking number in the invoice history.',
    noRecords: 'No invoices yet',
    colNo: 'Request number',
    colTitle: 'Invoice title',
    colKind: 'Type',
    colAmount: 'Amount',
    colNumber: 'Invoice number',
    colApplied: 'Requested',
    numberIs: 'Invoice number {number}',
    expressIs: 'Courier {express}',
    download: 'Download',
    express: 'Tracking',
    details: 'Details',
    detailTitle: 'Invoice details',
    issuedAt: 'Issued',
    reviewedAt: 'Handled',
    cancelApply: 'Cancel request',
    cancel: 'Cancel',
    cancelTitle: 'Cancel this invoice request?',
    cancelContent: '¥{amount} becomes available to invoice again.',
    cancelled: 'Cancelled',
    profilesNote: '{n} of {max} added',
    noProfiles: 'No invoice titles yet',
    isDefault: 'Default',
    makeDefault: 'Make default',
    defaultSet: 'Default title set',
    taxNo: 'Taxpayer ID',
    taxNoPlaceholder: '15–20 digits or capital letters',
    registered: 'Registered',
    bankShort: 'Bank',
    email: 'Email',
    emailHelp: 'Electronic invoices are sent here',
    mailTo: 'Mail to',
    specialSection: 'Required for special VAT invoices',
    address: 'Registered address',
    phone: 'Registered phone',
    bank: 'Bank',
    bankAccount: 'Bank account',
    deliverySection: 'Delivery',
    recipient: 'Recipient',
    recipientPhone: 'Phone',
    mailAddress: 'Mailing address',
    setDefault: 'Make this the default title',
    profileSaved: 'Invoice title saved',
    deleteTitle: 'Delete this invoice title?',
    deleteContent: 'Invoice requests already made for "{title}" are not affected.',
  },
  refund: {
    title: 'Refunds',
    statRefunded: 'Total refunded',
    statOpen: 'Refundable orders',
    statPending: 'In progress',
    tabApply: 'Request',
    tabRecords: 'History',
    closed: 'Refunds cannot be requested online. Contact the administrator for a refund',
    termsTitle: 'Refund terms',
    termsTopUp: 'Top-ups can be refunded within {n} days of payment, back to the Alipay account that paid. The amount leaves your balance when you request it.',
    termsPackage: 'Package orders can be refunded within {n} days of purchase, to your balance, and the time the order bought is taken off the package.',
    termsInvoiced: 'Invoiced money cannot be refunded.',
    noOpen: 'No orders can be refunded right now',
    target_topup: 'Top-up',
    target_package: 'Package',
    topUpTitle: 'Alipay top-up',
    colTarget: 'Type',
    colOrderNo: 'Order number',
    colWhat: 'Item',
    colPaid: 'Paid',
    colMax: 'Refundable',
    colPaidAt: 'Paid on',
    colDeadline: 'Refund by',
    ask: 'Refund',
    askTitle: 'Request a refund',
    maxIs: 'Up to ¥{amount}',
    deadlineIs: 'refund by {date}',
    amount: 'Amount',
    reason: 'Reason',
    reasonPlaceholder: 'Tell us why, so we can do better',
    whereTopUp: 'The refund goes back to the Alipay account that paid. The amount leaves your balance once you submit, and returns to it if the refund is rejected or cancelled.',
    wherePackage: 'The refund goes to your balance, and the time this order bought is taken off the package, which may make it expire sooner.',
    submit: 'Submit request',
    applied: 'Refund requested for review',
    noRecords: 'No refunds yet',
    colNo: 'Refund number',
    colOrder: 'Order',
    colAmount: 'Amount',
    colReason: 'Reason',
    colApplied: 'Requested',
    failedFor: 'The Alipay refund did not go through: {reason}. We are looking into it',
    partly: '¥{asked} requested, ¥{paid} refunded',
    cancel: 'Cancel',
    cancelTitle: 'Cancel this refund request?',
    cancelContent: '¥{amount} will no longer be refunded.',
    cancelContentTopUp: '¥{amount} will return to your balance.',
    cancelled: 'Cancelled',
  },
  // 购买套餐, and paying for a package (CheckoutModal).
  store: {
    title: 'Buy a package',
    listTitle: 'Packages',
    count: '{n} on sale',
    period_month: 'Monthly',
    period_quarter: 'Quarterly',
    period_year: 'Yearly',
    per_month: '/mo',
    per_quarter: '/qtr',
    per_year: '/yr',
    months_month: '1 month',
    months_quarter: '3 months',
    months_year: '1 year',
    noPeriod: '{period} not offered',
    save: 'Save ¥{amount} vs monthly',
    perMonth: '¥{amount} a month',
    unlimited: 'Unlimited',
    allGroups: 'All',
    ungrouped: 'Other',
    held: 'Owned',
    heldOne: 'You hold this package until {date}',
    heldMany: 'You hold {n} of this package',
    specTitle: 'Capacity',
    featureTitle: 'Features',
    spec_traffic: 'Traffic / month',
    spec_requests: 'Requests / month',
    spec_bandwidth: 'Bandwidth',
    spec_connections: 'Connections',
    spec_sites: 'Sites',
    spec_hostnames: 'Hostnames',
    spec_apexes: 'Apex domains',
    spec_rules: 'Rules per site',
    spec_l4: 'Acceleration rules',
    spec_cc: 'CC protection',
    spec_ddos: 'DDoS protection',
    spec_logs: 'Log retention',
    v_requests: '{n}',
    v_count: '{n}',
    v_rules: '{n}',
    v_days: '{n} days',
    h_traffic: '{n} traffic a month',
    h_trafficAll: 'Unlimited traffic',
    h_bandwidth: '{n} bandwidth',
    h_bandwidthAll: 'Unlimited bandwidth',
    h_sites: '{n} sites',
    h_sitesAll: 'Unlimited sites',
    compareTitle: 'Compare packages',
    compareCol: 'Capacity & features',
    included: 'Included',
    excluded: 'Not included',
    renew: 'Renew',
    buy: 'Buy now',
    balanceLine: 'Packages are paid from your balance: ¥{amount} now',
    topUp: 'Add funds',
    closedTitle: 'Packages are not sold online yet',
    closedSub: 'To buy or upgrade a package, file a ticket and we will help.',
    contact: 'File a ticket',
    toMine: 'My packages',
    nothingOnSale: 'No packages on sale',
    buyTitle: 'Buy a package',
    renewTitle: 'Renew a package',
    coPackage: 'Package',
    coPeriod: 'Period',
    coTerm: 'Service period',
    coBalance: 'Balance',
    coDue: 'Due',
    termValue: '{start} to {end}',
    noPrice: 'This package has no price yet. Contact support',
    pay: 'Pay ¥{amount}',
    payPlain: 'Pay',
    short: 'Your balance is ¥{amount} short',
    afterPay: 'Balance after paying: ¥{amount}',
    renewed: '"{name}" renewed',
    doneTitle: 'Package bought',
    doneSub: '"{name}" is in force until {date}',
    doneOrder: 'Order {no}',
    toSites: 'Add a site',
  },
  finance: {
    state: {
      remit: {
        pending: 'Pending',
        approved: 'Credited',
        rejected: 'Rejected',
        cancelled: 'Cancelled',
      },
      withdraw: {
        pending: 'Pending',
        processing: 'Paying out',
        success: 'Paid out',
        failed: 'Payout issue',
        rejected: 'Rejected',
        cancelled: 'Cancelled',
      },
      invoice: {
        pending: 'Pending',
        issued: 'Issued',
        rejected: 'Rejected',
        cancelled: 'Cancelled',
      },
      refund: {
        pending: 'Pending',
        processing: 'Refunding',
        success: 'Refunded',
        failed: 'Refund issue',
        rejected: 'Rejected',
        cancelled: 'Cancelled',
      },
    },
  },
  // 工单中心.
  tickets: {
    confidential: 'Send confidentially',
    confidentialHelp: 'For passwords, keys and the like: the message is masked until opened, never shown in mail or in-app messages, and its content and files are destroyed when the ticket closes.',
    title: 'My tickets',
    newTitle: 'New ticket',
    detailTitle: 'Ticket',
    unit: 'tickets',
    statAll: 'All tickets',
    statActive: 'In progress',
    statReplied: 'Answered',
    statClosed: 'Closed',
    listTitle: 'Tickets',
    listCount: '{n} in all',
    searchPlaceholder: 'Search descriptions or ticket numbers',
    filterAll: 'All states',
    filter_active: 'In progress',
    filter_replied: 'Answered',
    filter_closed: 'Closed',
    resetFilters: 'Reset',
    empty: 'No tickets yet',
    noMatch: 'No tickets match',
    unread: 'New reply',
    colNo: 'Ticket no.',
    colTitle: 'Description',
    colCategory: 'Category',
    colPriority: 'Priority',
    colUpdated: 'Updated',
    view: 'View',
    close: 'Close',
    closeTicket: 'Close ticket',
    closeTitle: 'Close ticket',
    closeContent: 'Close "{title}"? A closed ticket cannot be replied to; a new question needs a new ticket.',
    closed: 'Ticket closed',
    updatedAt: 'Updated {time}',
    status_open: 'Open',
    status_processing: 'In progress',
    status_replied: 'Answered',
    status_closed: 'Closed',
    priority_normal: 'Normal',
    priority_high: 'High',
    priority_urgent: 'Urgent',
    priorityHint_normal: 'A question or a setting; nothing is broken',
    priorityHint_high: 'Something is not working; the business is affected',
    priorityHint_urgent: 'The service is down; the site cannot be reached',
    category: 'Category',
    categoryPlaceholder: 'Choose a category',
    priority: 'Priority',
    site: 'Site',
    sitePlaceholder: 'The site this is about (optional)',
    bodyHelp: 'One problem per ticket. Name the addresses, when it started and the error to find it sooner. Never put passwords or keys in a ticket',
    files: 'Files',
    body: 'Description',
    bodyPlaceholder: 'Tell us as much as you can: what happens, since when, the addresses involved, the error, and what you have tried. Paste screenshots straight in.',
    bodyRequired: 'Describe the problem',
    submit: 'Submit',
    filed: 'Ticket submitted. Support will be with you shortly',
    closedForNew: 'Tickets cannot be filed online',
    closedForNewSub: 'Contact support another way for help. Tickets already filed can still be read and answered in My tickets.',
    toList: 'Back to tickets',
    filedAt: 'Filed',
    repliedTip: '{support} has replied. If the problem is solved, close the ticket; left unanswered for long, it closes by itself.',
    replyPlaceholder: 'Describe your question; paste or drop pictures in. Our engineers will answer as soon as they can — leaving this page does not close the ticket.',
    send: 'Send',
    replied: 'Reply sent',
    missing: 'Ticket not found',
    missingSub: 'It may have been deleted, or it belongs to another account.',
    rateTitle: 'Rating',
    closedBy_customer: 'You closed the ticket on {time}',
    closedBy_staff: '{support} closed the ticket on {time}',
    closedBy_auto: 'The ticket went unanswered and closed by itself on {time}',
    rateAsk: 'How satisfied are you with the help you got?',
    ratePick: 'Click a star to rate',
    commentPlaceholder: 'Anything you would like to add (optional)',
    rateSubmit: 'Submit rating',
    rated: 'Thank you for rating',
    ratedAt: 'Rated on {time}',
    stars_1: 'Very poor',
    stars_2: 'Poor',
    stars_3: 'Okay',
    stars_4: 'Good',
    stars_5: 'Excellent',
    infoTitle: 'Details',
    account: 'Submitted by',
    contactEmail: 'Contact email',
    reveal: 'Show',
    conceal: 'Hide',
    stepFiled: 'Submitted',
    stepAccepted: 'Accepted',
    stepResolved: 'Resolved',
    stepRated: 'Rated',
    stepWaiting: 'Waiting',
    stepHandling: 'In progress',
    stepReplied: 'Support replied',
    stepToRate: 'Not rated yet',
  },
  // ---- 我的投诉 ----
  l4: {
    title: 'Acceleration Rules',
    unit: '',
    statAll: 'All rules',
    statActive: 'Running',
    statDisabled: 'Disabled',
    statSuspended: 'Suspended',
    listTitle: 'Acceleration rules',
    listCount: '{n} in all',
    filterAll: 'All states',
    resetFilters: 'Reset',
    state_active: 'Running',
    state_disabled: 'Disabled',
    state_suspended: 'Suspended',
    reason_plan_expired: 'Package expired',
    reason_traffic_exceeded: 'Traffic used up',
    reason_requests_exceeded: 'Requests used up',
    reason_no_feature: 'Not in package',
    colName: 'Name',
    colAccess: 'Access address',
    colOrigins: 'Origins',
    noAccessHost: 'Package has no line',
    proto_tcp: 'TCP',
    proto_udp: 'UDP',
    proto_tcp_udp: 'TCP+UDP',
    stats: 'Stats',
    more: 'More',
    enable: 'Enable',
    disable: 'Disable',
    enabled: 'Enabled',
    disabled: 'Disabled',
    deleted: 'Deleted',
    disableTitle: 'Disable rule',
    disableContent: 'Disabling “{name}” closes port {port} at once and drops the connections on it.',
    deleteTitle: 'Delete rule',
    deleteContent: 'Deleting “{name}” closes and frees port {port} at once and drops the connections on it. This cannot be undone.',
    noMatch: 'No rule matches',
    create: 'New rule',
    submitCreate: 'Create',
    save: 'Save',
    saved: 'Saved',
    created: 'Rule created on port {port}',
    searchPlaceholder: 'Search name, port or origin',
    edit: 'Edit',
    empty: 'No rules yet. Create one to accelerate a TCP/UDP port to your origin.',
    noPackage: 'None of your packages includes App Acceleration. Buy or upgrade to a package with it to create rules.',
    toStore: 'Browse packages',
    createTitle: 'New acceleration rule',
    editTitle: 'Edit acceleration rule',
    formIntro: 'Visitors connect to the access address on the listen port; the edge nodes relay the TCP/UDP traffic to your origins untouched — for games, remote desktop, databases, MQTT and other non-HTTP services.',
    accessPreview: 'Access address: ',
    portAuto: 'assigned',
    idToken: 'rule-id',
    package: 'Package',
    packageHint: 'The traffic counts against this package, and its line decides the access address',
    packagePlaceholder: 'Choose a package',
    packageNoL4: 'Not included',
    packageFull: 'Full',
    packageUsed: '{used}/{max} used',
    name: 'Name',
    namePlaceholder: 'e.g. Game server, Remote desktop',
    protocol: 'Protocol',
    listenPort: 'Listen port',
    listenPortPlaceholder: 'Leave empty to assign',
    listenPortHint: 'Available: {min}–{max}',
    origins: 'Origins',
    originsPlaceholder: 'One a line, e.g.\n203.0.113.10:7777\ngame.example.com:7777',
    originsHint: 'Up to {max}, as IP:port or host:port, IPv6 as [address]:port; the next one is tried when one does not answer',
    balance: 'Load balancing',
    balance_round_robin: 'Round robin',
    balance_ip_hash: 'Source IP hash',
    balanceHint_round_robin: 'New connections go to each origin in turn',
    balanceHint_ip_hash: 'One visitor IP always reaches the same origin — for services that keep sessions',
    proxyProtocol: 'PROXY protocol',
    proxy_off: 'Off',
    proxy_v1: 'v1',
    proxy_v2: 'v2',
    proxyHint: 'Tells the origin the visitor’s real IP. The origin must accept that PROXY version or the connection fails. TCP only.',
    proxyUdpHint: 'PROXY protocol applies to TCP only',
    remark: 'Remark',
    remarkPlaceholder: 'Optional',
  },
  l4stats: {
    title: '{name} · Statistics',
    range_1h: '1h',
    range_today: 'Today',
    range_yesterday: 'Yesterday',
    range_7d: '7d',
    range_30d: '30d',
    loadFailed: 'Could not load the statistics',
    noLogs: 'Log storage is not connected, so there are no statistics yet',
    fig_trafficOut: 'Traffic out',
    fig_peakOut: 'Peak bandwidth out',
    fig_conns: 'New connections',
    fig_active: 'Peak concurrent',
    upNote: 'In {v}',
    connsNote: '{failed} no origin · {refused} refused',
    activeNote: 'Open at the same moment',
    tab_bandwidth: 'Bandwidth',
    tab_traffic: 'Traffic',
    tab_conns: 'Connections',
    tab_active: 'Concurrent',
    down: 'Out',
    up: 'In',
    newConns: 'New',
    failed: 'No origin',
    refused: 'Refused',
    activeLine: 'Concurrent',
    clientsTitle: 'Top visitors',
    colIp: 'Visitor IP',
    colConns: 'Connections',
    noClients: 'No visitors in this window',
  },
  complaints: {
    title: 'My complaints',
    unit: '',
    statAll: 'All complaints',
    statOpen: 'In progress',
    statResolved: 'Resolved',
    statWithdrawn: 'Withdrawn',
    listTitle: 'Complaints',
    listCount: '{n} in all',
    fileTitle: 'File a complaint',
    searchPlaceholder: 'Search number or text',
    filterAll: 'All states',
    filter_open: 'In progress',
    filter_resolved: 'Resolved',
    filter_withdrawn: 'Withdrawn',
    noMatch: 'No complaints match',
    empty: 'No complaints yet. If the service on a ticket lets you down, use Complain on its page.',
    cat_attitude: 'Attitude',
    cat_speed: 'Response time',
    cat_result: 'Outcome',
    cat_outage: 'Outage',
    cat_billing: 'Billing',
    cat_other: 'Other',
    status_pending: 'Pending',
    status_processing: 'In progress',
    status_resolved: 'Resolved',
    status_withdrawn: 'Withdrawn',
    aboutTicket: 'Ticket {no}',
    view: 'View',
    withdraw: 'Withdraw',
    withdrawTitle: 'Withdraw the complaint',
    withdrawContent: 'Complaint {no} will no longer be looked into. Withdraw it?',
    withdrawn: 'Complaint withdrawn',
    colComplaint: 'Complaint',
    colNo: 'Number',
    category: 'About',
    content: 'What happened',
    ticket: 'Ticket',
    noTicket: 'No ticket',
    filedAt: 'Filed',
    detailTitle: 'Complaint',
    result: 'Our answer',
    handledAt: 'Answered {time}',
    withdrawnNote: 'You withdrew this complaint.',
    waiting_pending: 'We have your complaint and someone will look into it soon. The answer will appear here.',
    waiting_processing: 'Someone is looking into your complaint. The answer will appear here.',
    close: 'Close',
    filed: 'Complaint filed',
    submit: 'File complaint',
    intro: 'A complaint is followed up by a dedicated person. The answer appears in My complaints.',
    contentPlaceholder: 'Say what happened, when, and what you would like us to do.',
    complain: 'Complain',
    viewMine: 'View complaint',
  },
  // A message's files and the conversation: components/tickets, the same
  // files in both consoles, so the same keys.
  ticketFiles: {
    add: 'Attach files',
    tip: 'Up to {max} files of {mb} MB each; screenshots can be pasted',
    uploading: 'Uploading…',
    tooMany: 'At most {max} files',
    tooLarge: '"{name}" is over {max} MB',
    empty: '"{name}" is empty',
    failed: '"{name}" could not be uploaded',
    downloadFailed: 'Download failed',
  },
  richEditor: {
    image: 'Insert picture',
    bold: 'Bold',
    italic: 'Italic',
    underline: 'Underline',
    bulletList: 'Bulleted list',
    numberList: 'Numbered list',
    link: 'Insert link',
    unlink: 'Remove link',
    attach: 'Attach files',
    linkPlaceholder: 'Link address',
    linkApply: 'OK',
    badLink: 'That is not a valid link',
  },
  ticketThread: {
    secretTag: 'Confidential',
    secretReveal: 'Click to view',
    secretHide: 'Hide',
    secretPurged: 'Confidential content was destroyed when the ticket closed',
    newMessages: 'New messages, click to view',
    newSpoken: 'You have a new ticket message',
    infoBody: 'Description',
    infoFiles: 'Attachments',
    infoSite: 'Site',
    stage_filed: 'Created',
    stage_processing: 'In progress',
    stage_closed: 'Closed',
    me: 'Me',
    support: 'Support',
    roleCustomer: 'Customer',
    roleStaff: 'Support',
    note: 'Internal note',
    ev_accepted: 'Support took the ticket on',
    ev_assigned: 'Handed to {name}',
    ev_assignedSelf: '{name} took the ticket',
    ev_unassigned: 'Unassigned',
    ev_priority: 'Priority set to {priority}',
    ev_closed_customer: 'You closed the ticket',
    ev_closed_staff: 'Support closed the ticket',
    ev_closed_auto: 'Closed by itself after going unanswered',
    assistant: 'Service assistant',
    as_filed: 'Ticket received',
    as_filedText: 'We have your question. {support} will get to it soon, and you will hear about every step here. Add anything you think of below.',
    as_accepted: '{support} picked up your ticket',
    as_acceptedText: '{support} is working on your question and will reply here as soon as there is news.',
    as_closed_customer: 'You closed the ticket',
    as_closed_customerText: 'Thanks for letting us know. If the problem is not solved, file a new ticket.',
    as_closed_staff: '{support} closed the ticket',
    as_closed_staffText: 'Support has finished with this question. If anything is still unclear, file a new ticket.',
    as_closed_auto: 'The ticket closed itself',
    as_closed_autoText: 'There were no new replies for a long time, so the ticket was closed. If anything is still unclear, file a new ticket.',
  },
  myPackages: {
    autoEdit: 'Change',
    autoFailed_insufficientBalance: '{pkg} did not renew automatically: the balance is short. Top up and it renews by itself.',
    autoFailed_other: '{pkg} did not renew automatically: it cannot be renewed online. Contact support to renew.',
    autoFailed_periodUnavailable: '{pkg} did not renew automatically: it is no longer sold for that period. Change the period.',
    autoOff: 'Off',
    autoOn: 'Renews for {period}',
    autoWhen: ', charged 3 days before expiry',
    autoRenew: 'Auto-renew',
    title: 'My packages',
    intro: 'Your packages and when they expire. Each site belongs to the package chosen when it was created; once that package expires, its sites are suspended and visitors see a "Service suspended" page. Service resumes within a minute of renewing.',
    lapsedAlert: 'A package has expired and its {n} site(s) are suspended. Contact support to renew.',
    endingAlert: 'A package expires within 7 days. Contact support to renew so your sites are not suspended.',
    packageCol: 'Package',
    stateCol: 'State',
    startedCol: 'Started',
    expiresCol: 'Expires',
    remainingCol: 'Remaining',
    sitesCol: 'Sites',
    state_active: 'Active',
    state_expiring: 'Expiring',
    state_expired: 'Expired',
    daysLeft: '{n} days',
    hoursLeft: '{n} hours',
    expiredAgo: 'Expired {n} days ago',
    statActive: 'Active packages',
    statExpiring: 'Expiring',
    statExpired: 'Expired',
    statSites: 'Sites on packages',
    startedAt: 'Started',
    expiresAt: 'Expires',
    meter_traffic: 'Traffic this period',
    meter_requests: 'Requests this period',
    meter_bandwidth: 'Bandwidth now',
    meter_connections: 'Connections now',
    unlimited: 'Unlimited',
    sitesValue: '{n} site(s)',
    resets: 'Period resets {time}',
    noUsage: 'This package is not metered.',
    empty: 'No packages yet. Contact support to get one.',
    // The title names what is counted; English needs no unit word.
    unitSubs: '',
    count_sites: 'Sites',
    count_hostnames: 'Domains',
    count_apexes: 'Apex domains',
    count_rules: 'Rules per site',
    count_l4: 'Acceleration rules',
    rulesPerSiteValue: '{n}',
    featureOff: 'Not included',
    lapsedAlertRenew: 'A package has expired and its {n} site(s) are suspended. Renew it and they resume within a minute.',
    endingAlertRenew: 'A package expires within 7 days. Renew it, or turn on auto-renew, so your sites are not suspended.',
    emptyStore: 'No packages yet',
    buy: 'Buy a package',
    buySub: 'Paid from your balance, active at once',
    renew: 'Renew',
  },
  assistant: {
    defaultName: 'AI Assistant',
    newChat: 'New chat',
    history: 'History',
    expand: 'Expand',
    shrink: 'Restore',
    close: 'Close',
    back: 'Back',
    hello: "Hi, I'm {name}",
    helloSub: 'Ask me about adding sites, troubleshooting, packages and billing.',
    canDo: 'I can help with',
    act_onboard: 'Add a website',
    act_onboardSub: 'Get a site accelerated, step by step',
    act_diagnose: 'Diagnose an error',
    act_diagnoseSub: '502s, certificate errors, slow pages',
    act_billing: 'Packages & billing',
    act_billingSub: 'Choosing, renewing, top-ups and invoices',
    actOnboardAsk: 'I want to accelerate my website. What do I need to do?',
    actBillingAsk: 'Which package should I choose, and how do I renew it?',
    actDiagnosePrefill: 'Please help me troubleshoot my site ',
    mayAsk: 'You might ask',
    placeholder: 'Ask anything — Shift + Enter for a new line',
    send: 'Send',
    stop: 'Stop',
    left: '{n} questions left today',
    disclaimer: 'Generated by AI; for reference only',
    tryAsk: 'Try: {q}',
    waiting: 'Answering',
    thinking: 'Thinking',
    stopped: 'Stopped',
    failedBefore: 'This answer did not finish; try again',
    retry: 'Retry',
    copy: 'Copy',
    copied: 'Copied',
    historyEmpty: 'No conversations yet',
    deleteConfirm: 'Delete this conversation?',
    deleted: 'Deleted',
  },
  autoRenew: {
    title: 'Auto-renew',
    period: 'Renew for',
    charge: 'Each renewal',
    when: 'Charged',
    whenValue: '3 days before expiry, from {time}',
    whenSoon: 'Within 10 minutes of turning it on',
    short: 'The balance does not cover the next renewal. Top up ¥{amount} before {time}, or it will fail.',
    shortNow: 'The balance does not cover this renewal. Top up ¥{amount}.',
    note: 'Renewed from the balance 3 days before expiry. You are told in the console and by mail whether each renewal went through. Turn it off any time.',
    turnOn: 'Turn on',
    turnedOn: 'Auto-renew turned on',
    turnedOff: 'Auto-renew turned off',
    saved: 'Saved',
  },
  credentials: {
    title: 'API Keys',
    sectionTitle: 'Open API credentials',
    intro: 'AK/SK pairs authenticate Open API calls. The secret is shown once at creation — save it now.',
    nameCol: 'Label',
    accessKeyCol: 'Access key',
    statusCol: 'Status',
    lastUsedCol: 'Last used',
    createdCol: 'Created',
    create: 'New key',
    nameLabel: 'Label (optional)',
    namePlaceholder: 'e.g. CI deploy bot',
    disable: 'Disable',
    disableConfirmTitle: 'Disable key',
    disableConfirm: 'Calls using this key start failing at once, and it cannot be turned back on.',
    secretModalTitle: 'Copy the secret now',
    secretWarn: 'The secret is shown only once. You cannot retrieve it after closing this dialog.',
    secretCopied: 'Copied',
    statusActive: 'Active',
    statusDisabled: 'Disabled',
    createSuccess: 'Key created',
    createFailed: 'Failed to create',
    disableSuccess: 'Disabled',
    disableFailed: 'Failed to disable',
    statTotal: 'Keys',
    statActive: 'Active',
    statDisabled: 'Disabled',
    statRecent: 'Used in {n} days',
    listTitle: 'All keys',
    listCount: '{n} in all',
    noName: 'No note',
    never: 'Never',
    nameHint: 'Say where the key is used, so it is easy to tell apart when disabling it later.',
    empty: 'No API keys yet',
    noOpenapi: 'None of your packages includes the Open API: existing keys are paused and no new one can be made. They work again on a package that includes it.',
    noOpenapiShort: 'The Open API is not in your packages',
    statusPaused: 'Paused',
  },
  security: {
    title: 'Security events',
    statBlocked: 'Blocked',
    statEvents: '{n} events in all',
    statIps: 'Attacking IPs',
    statWaf: 'WAF blocks',
    statCc: 'CC blocks',
    unitTimes: 'times',
    unitIps: 'IPs',
    listTitle: 'Events',
    window_1: 'Last hour',
    window_24: 'Last 24 hours',
    window_168: 'Last 7 days',
    range_1: '1h',
    range_24: '24h',
    range_168: '7d',
    filterAllSites: 'All sites',
    filterAllModules: 'All modules',
    filterAllActions: 'All actions',
    ipPlaceholder: 'Client IP',
    timeCol: 'Time',
    hostCol: 'Hostname',
    ipCol: 'Client IP',
    moduleCol: 'Module',
    actionCol: 'Action',
    ruleCol: 'Rule',
    uriCol: 'Request',
    pageN: 'Page {n}',
    prev: 'Previous',
    next: 'Next',
    noLogs: 'Log storage is not connected, so there are no security events yet',
    empty: 'No security events in this window',
    module_waf: 'WAF',
    module_cc: 'CC',
    module_acl: 'ACL',
    module_bot: 'Bot',
    module_geo: 'Region',
    module_iplist: 'IP list',
    module_challenge: 'Challenge',
    module_edge: 'Edge',
    module_guard: 'Node guard',
    module_site: 'Site',
    module_quota: 'Quota',
    module_upstream: 'Origin',
    action_pass: 'Pass',
    action_redirect_https: 'HTTPS redirect',
    action_challenge: 'Challenge',
    action_challenge_verify: 'Answer',
    action_block: 'Denylist block',
    action_block_acl: 'ACL block',
    action_block_waf: 'WAF block',
    action_block_bot: 'Bot block',
    action_block_cc: 'CC block',
    action_block_ban: 'Ban block',
    action_block_geo: 'Region block',
    action_log_geo: 'Region log',
    action_block_cross_site: 'Cross-site block',
    action_ban: 'Failure ban',
    action_suspended: 'Site suspended',
    action_conn_limit: 'Connection limit',
    action_shed: 'Origin overload',
    action_websocket_off: 'WebSocket off',
  },
  menu: {
    grpSites: 'Sites',
    grpL4: 'App Acceleration',
    grpSecurity: 'Security',
    grpBilling: 'Billing',
    grpAccount: 'Account',
    grpFinance: 'Finance',
    grpTickets: 'Support',
  },
  // Messages the control plane produced, keyed by the stable code it ships
  // alongside the English text. The console looks the code up here so an
  // operator reads guidance in their own language; anything missing falls
  // back to the server's English rather than showing a bare key.
  quota: {
    usageCol: 'This period',
    traffic: 'Traffic',
    requests: 'Requests',
    bandwidth: 'Bandwidth',
    connections: 'Connections',
    unlimited: 'unlimited',
    withBonus: 'incl. top-up',
    resetsAt: 'resets {time}',
    exceeded_traffic_exceeded: 'Traffic used up',
    exceeded_requests_exceeded: 'Requests used up',
    exceededAlert_traffic_exceeded: 'Package "{pkg}" has used up this period\'s traffic. Its sites are suspended and visitors see the "Traffic exceeded" page. Service resumes at {time} when the next period starts, or sooner if the plan is topped up or upgraded.',
    exceededAlert_requests_exceeded: 'Package "{pkg}" has used up this period\'s requests. Its sites are suspended and visitors see the "Request limit exceeded" page. Service resumes at {time} when the next period starts, or sooner if the plan is topped up or upgraded.',
    topUp: 'Top up',
    reset: 'Reset usage',
    topUpTitle: 'Top up this period',
    topUpIntro: 'Add traffic or requests to {user}\'s "{pkg}" for the current period only. Sites suspended for running out resume within a minute.',
    millionSuffix: 'million',
    topUpDone: 'Topped up',
    resetTitle: 'Reset this period\'s usage',
    resetConfirm: 'Set the traffic and requests {user}\'s "{pkg}" used this period back to zero? Sites suspended for running out resume within a minute.',
    resetDone: 'Reset',
  },
  entitlements: {
    feature_waf: 'WAF',
    feature_custom_cc: 'Custom CC rules',
    feature_websocket: 'WebSocket',
    feature_http3: 'HTTP/3',
    feature_custom_cert: 'Custom certificates',
    feature_openapi: 'OpenAPI',
    feature_l4: 'App Acceleration',
  },
  // 人机验证 in the pages (utils/captcha.ts).
  captcha: {
    title: 'Security check',
    googleHint: 'Tick the box below and you will continue automatically.',
    loadFailed: 'The security check could not load. Check your network and try again',
    localTip: 'Slide to fit the piece',
    localPassed: 'Verified',
    localMissed: 'Not quite. Try again',
    localRefresh: 'New puzzle',
    localRetry: 'Retry',
    tencentError: 'Tencent Cloud Captcha failed (error {code}). Try again later',
  },
  smsCode: {
    send: 'Get code',
    resend: 'Resend',
    wait: 'Resend in {n}s',
    placeholder: '6-digit code',
    sent: 'Code sent to {phone}',
    failed: 'Could not send the code',
    required: 'Enter the 6-digit code from the text message',
  },
  accountCenter: {
    title: 'Account center',
    basic: 'Basic info',
    realname: 'ID verification',
    member: 'Level & VIP',
  },
  member: {
    vipTip: 'VIP{n} member',
    lvTip: 'LV{lv} · {cur}/{need} EXP',
    lvTipTop: 'LV{lv} · top level',
    top: 'Top level',
    vipTitle: 'VIP',
    vipPerkTicket: "Perk: priority support. Your tickets come first in support's queue.",
    vipPerkNone: 'Perks are as the platform announces them.',
    vipNone: 'You are not a VIP yet',
    vipNoneHelp: 'Your VIP level follows your growth points, which the platform gives.',
    vipNoneHelpTicket: "Your VIP level follows your growth points, which the platform gives; a VIP's tickets are handled first.",
    vipAsk: 'Ask about VIP',
    notVip: 'Not a VIP',
    growthUnit: 'growth points',
    vipToNext: '{n} growth points to VIP{lv}',
    vipTotal: '{n} growth points in all',
    vipTop: 'Top VIP level',
    lvTitle: 'My level',
    expUnit: 'EXP',
    toNext: '{n} EXP to LV{lv}',
    total: '{n} EXP in all',
    ladderCaption: 'The EXP each level takes in all',
    earnTitle: 'Earn EXP',
    daily: 'Daily visit',
    dailyHelp: 'Given the first time you open the console each day, China time.',
    dailyDone: 'Earned today',
    dailyNot: 'Not yet today',
    task_realname: 'Verify your identity',
    task_phone: 'Bind a phone',
    task_guard: 'Turn on sign-in protection',
    task_site: 'Add your first site',
    taskHelp_realname: 'Pass individual or enterprise verification.',
    taskHelp_phone: 'Prove a phone number with a texted code.',
    taskHelp_guard: 'Bind an MFA device, or turn on texted codes at sign-in.',
    taskHelp_site: 'Add a site in the site list.',
    taskGo_realname: 'Verify',
    taskGo_phone: 'Bind',
    taskGo_guard: 'Turn on',
    taskGo_site: 'Add',
    taskDone: 'Done',
    taskRecord: 'Starter task · {task}',
    adjust: 'Platform change',
    adjustRecord: 'Platform change · {note}',
    recordsTitle: 'EXP history',
    recordsSub: 'latest {n}',
    recordsEmpty: 'No EXP yet',
    colTime: 'Time',
    colSource: 'Source',
    colExp: 'EXP',
    growthTitle: 'Growth history',
    colNote: 'Note',
    colGrowth: 'Growth points',
    colAfter: 'After',
    after: 'after: {n}',
  },
  profile: {
    title: 'Profile',
    leaveTitle: 'Discard unsaved changes?',
    leaveBody: 'Basic info has changes that are not saved. They will be lost if you switch to another tab.',
    leaveOk: 'Discard and switch',
    changeAvatar: 'Change avatar',
    addAvatar: 'Upload avatar',
    remove: 'Remove',
    removeTitle: 'Remove avatar',
    removeContent: 'Your avatar goes back to your username\'s first letter.',
    avatarSaved: 'Avatar updated',
    avatarRemoved: 'Avatar removed',
    avatarHint: 'JPG, PNG or GIF, cropped before it is uploaded',
    pickTooLarge: 'A picture is at most {max} MB',
    factId: 'Account ID',
    factUsername: 'Username',
    factCreated: 'Signed up',
    factRealname: 'ID verification',
    factEmail: 'Email',
    factPhone: 'Phone',
    change: 'Change',
    bind: 'Bind',
    none: 'Not bound',
    basicTitle: 'Basics',
    nickname: 'Nickname',
    nicknameHelp: 'Shown at the console\'s top right and on the dashboard; your username when empty',
    contactTitle: 'Contact',
    contactIntro: 'For support working on your tickets, or when your email and phone do not reach you. Only the platform\'s staff see it.',
    qqPlaceholder: 'QQ number',
    wechat: 'WeChat ID',
    wechatPlaceholder: 'WeChat ID or the mobile it is bound to',
    backupName: 'Backup contact',
    backupNamePlaceholder: 'Name',
    backupPhone: 'Backup phone',
    backupPhonePlaceholder: 'Mobile or landline, e.g. 010-12345678',
    companyTitle: 'Company',
    companyIntro: 'Helps us suggest the right packages and setup. Individuals may give just the industry.',
    company: 'Company name',
    companyPlaceholder: 'As on the business licence',
    industry: 'Industry',
    industryPlaceholder: 'Choose an industry',
    industry_internet: 'Internet / software',
    industry_ecommerce: 'E-commerce',
    industry_media: 'Video / live / media',
    industry_gaming: 'Gaming',
    industry_education: 'Education',
    industry_finance: 'Finance',
    industry_government: 'Government / public',
    industry_manufacturing: 'Manufacturing',
    industry_healthcare: 'Healthcare',
    industry_other: 'Other',
    teamSize: 'Team size',
    teamPlaceholder: 'Choose a team size',
    team_1_9: '1–9',
    team_10_49: '10–49',
    team_50_199: '50–199',
    team_200_999: '200–999',
    team_1000plus: '1000+',
    siteUsage: 'What your sites are for',
    siteUsagePlaceholder: 'e.g. company site, online shop, video on demand, downloads, API',
    addressTitle: 'Mailing address',
    addressIntro: 'Where paper invoices and contracts are sent.',
    region: 'Region',
    regionPlaceholder: 'Province / city',
    postcode: 'Postcode',
    country: 'Country or region',
    countryPlaceholder: 'Choose one; none is taken as mainland China',
    countryHelp: 'ID verification offers this country\'s methods: mainland China\'s own, or international ones elsewhere',
    countryLocked: 'Fixed once the account is verified',
    postcodePlaceholder: '6 digits',
    address: 'Address',
    addressPlaceholder: 'Street, number, floor',
    saved: 'Profile saved',
    cropTitle: 'Change avatar',
    cropPreview: 'Preview',
    cropZoom: 'Zoom',
    cropHint: 'Drag the picture to place it; scroll or use the slider to zoom',
    cropRepick: 'Choose another',
    cropSave: 'Save avatar',
  },
  saveBar: {
    label: 'Save',
    unsaved: 'Unsaved changes',
    reset: 'Discard changes',
    fixFirst: 'Fix the errors above first',
  },
  realname: {
    title: 'ID verification',
    state_unverified: 'Not verified',
    state_pending: 'Under review',
    state_rejected: 'Not approved',
    intro: 'Verify your identity to use everything the platform offers. The details are used only to verify you, and the ID number is stored encrypted.',
    introVerified: 'This account is verified. To change who it is verified as, file a ticket.',
    factType: 'Type',
    typeIndividual: 'Individual',
    typeEnterprise: 'Enterprise',
    factName: 'Name',
    factCompany: 'Company',
    factCertNo: 'ID number',
    factMethod: 'Method',
    via_alipay: 'Alipay · {method}',
    via_tencent: 'Tencent Cloud · {method}',
    via_aliyun: 'Alibaba Cloud · {method}',
    viaSupport: 'Reviewed by support',
    factTime: 'Verified at',
    personalTitle: 'Individual verification',
    by_alipay: 'Alipay',
    by_tencent: 'Tencent Cloud',
    by_aliyun: 'Alibaba Cloud',
    by_sumsub: 'Sumsub',
    by_veriff: 'Veriff',
    via_sumsub: 'Sumsub · {method}',
    via_veriff: 'Veriff · {method}',
    personalSub_sumsub: 'Verified by Sumsub',
    personalSub_veriff: 'Verified by Veriff',
    agree_sumsub: 'I agree to send my document photos and face images to Sumsub for identity verification',
    agree_veriff: 'I agree to send my document photos and face images to Veriff for identity verification',
    method_SUMSUB_IDV: 'Document and face check',
    method_VERIFF_IDV: 'Document and face check',
    methodHint_SUMSUB_IDV: 'Photograph a passport, ID card or driving licence on Sumsub\'s page, then a liveness selfie; most countries\' documents are accepted',
    methodHint_VERIFF_IDV: 'Photograph a passport, ID card or driving licence on Veriff\'s page, then a liveness selfie; most countries\' documents are accepted',
    country: 'Country or region',
    countryChange: 'Change',
    regionHint_cn: 'Accounts in mainland China use mainland China\'s methods',
    regionHint_intl: 'Accounts outside mainland China use international verification: a document and a face check',
    stepIntl: 'Document and face',
    scanTitle_intl: 'Open the verification page, or scan the code to go on with your phone',
    openTitle_intl: 'Open the verification page to check your document and face',
    intlFor: '{provider} · {method}',
    intlReview: 'It is reviewed once sent, usually within minutes; this page updates by itself',
    openIntl: 'Open the verification page',
    intlWaiting: 'No result yet; this page updates once the review is done',
    factDoc: 'Document',
    doc_PASSPORT: 'Passport',
    doc_ID_CARD: 'ID card',
    doc_DRIVERS: 'Driving licence',
    doc_RESIDENCE_PERMIT: 'Residence permit',
    personalSub_alipay: 'Verified by Alipay identity verification',
    personalSub_tencent: 'Verified by Tencent Cloud FaceID',
    personalSub_aliyun: 'Verified by Alibaba Cloud CPNS',
    offlineTitle: 'Online verification is not open yet',
    offlineSub: 'File a ticket to be verified; support will verify you after review.',
    toTicket: 'File a ticket',
    stepFill: 'Your details',
    stepAlipay: 'Alipay',
    stepWechat: 'WeChat face scan',
    stepCheck: 'Check',
    stepDone: 'Verified',
    method: 'Method',
    method_SMART_FACE: 'Quick',
    method_FACE: 'Face scan',
    method_CERT_PHOTO: 'ID card photo',
    method_CERT_PHOTO_FACE: 'ID card photo + face scan',
    method_TC_FACE: 'WeChat face scan',
    method_TC_ID2: 'Name + ID number',
    method_TC_PHONE3: 'Name + ID + mobile',
    methodHint_SMART_FACE: 'Alipay picks the check from your account; a verified Alipay account usually needs no photo, the quickest way',
    methodHint_FACE: 'A face scan in Alipay, compared with your ID card photo',
    methodHint_CERT_PHOTO: 'Photograph both sides of your ID card in Alipay',
    methodHint_CERT_PHOTO_FACE: 'Photograph your ID card, then a face scan: the strictest check',
    methodHint_TC_FACE: 'Scan with WeChat to open the page; a liveness check, then a match against the photo on record',
    methodHint_TC_ID2: 'Checks that the name goes with the ID number; answered at once, no face scan',
    methodHint_TC_PHONE3: 'Checks that the name, ID number and mobile number are one person\'s; answered at once, no face scan',
    method_ALI_ID2: 'Name + ID number',
    method_ALI_PHONE3: 'Name + ID + mobile',
    methodHint_ALI_ID2: 'Checks that the name goes with the ID number; answered at once, no face scan',
    methodHint_ALI_PHONE3: 'Checks, by the carrier\'s records, that the name, ID number and mobile number are one person\'s; answered at once',
    certName: 'Full name',
    certNamePlaceholder: 'As on your ID card',
    certNo: 'ID card number',
    certNoPlaceholder: '18-digit resident ID card number',
    phone: 'Mobile number',
    phonePlaceholder: 'Registered with your own ID card',
    phoneHelp: 'A mainland China mobile number registered in your name',
    fee: 'Fee',
    feeFree: 'Free this time',
    feeFreeLeft: '{n} free left, then ¥{fee} each',
    feeFromBalance: 'Paid from your balance (¥{balance})',
    feeShort: 'Not enough balance',
    toTopup: 'Top up',
    feeConfirmTitle: 'Pay the verification fee?',
    feeConfirm: '¥{fee} will be paid from your balance (¥{balance}). If the verification service does not take it on, the fee comes back.',
    feeConfirmOk: 'Pay and start',
    agree_alipay: 'I agree to send these details to Alipay to verify my identity',
    agree_tencent: 'I agree to send these details to Tencent Cloud to verify my identity',
    agree_aliyun: 'I agree to send these details to Alibaba Cloud to verify my identity',
    agreeRequired: 'Agree to send the details for verification first',
    start: 'Start',
    attemptsLeft: '{n} left today',
    attemptsNone: 'No verifications left today; try again tomorrow',
    scanTitle_alipay: 'Scan with Alipay to verify',
    scanTitle_wechat: 'Scan with WeChat for the face scan',
    openTitle_alipay: 'Finish verifying in Alipay',
    openTitle_wechat: 'Finish the face scan on your phone',
    scanFor: '{method} · {name} · {no}',
    left: 'Time left',
    openAlipay: 'Open Alipay',
    openFace: 'Start the face scan',
    done: 'I\'ve finished',
    refill: 'Edit details',
    passedToast: 'Verified',
    failedTitle: 'Not verified',
    failedSub: 'The identity check did not pass this time. Check your details and try again.',
    expiredTitle: 'Timed out',
    expiredSub: 'Verification was not finished within 30 minutes. Start again.',
    notTakenTitle: 'Could not be verified',
    notTakenSub: 'The verification service did not take this one on. It does not count against your free attempts, and any fee has come back. Try again later.',
    reason: 'Reason: {reason}',
    retry: 'Try again',
    enterpriseTitle: 'Enterprise verification',
    enterpriseRow: 'Enterprise',
    enterpriseDesc: 'Enterprises file a ticket with their business licence; support verifies the account after review.',
    enterpriseGo: 'File a ticket',
    ticketBody_enterprise: 'Enterprise verification\nCompany name:\nUnified social credit code:\nContact and phone:\n(Attach a photo of the business licence)',
    ticketBody_individual: 'Individual verification\nName:\nPhone:',
    doneTitle: 'Verification is over',
    doneSub: 'Go back to the page you started from to see the result.',
    doneGo: 'See the result',
  },
  subUsers: {
    title: 'Sub-users',
    statTotal: 'Sub-users',
    statActive: 'Enabled',
    statDisabled: 'Disabled',
    statRecent: 'Signed in, last {n} days',
    unit: '',
    listTitle: 'Sub-users',
    listCount: '{n} of up to {max}',
    create: 'New sub-user',
    full: 'An account can have at most {max} sub-users',
    intro: "Sub-users sign in with the main account ID, their own name and password, under Other ways to sign in › Sub-user on the sign-in page. A sub-user has the same permissions as the main account, but can't manage sub-users or change the main account's password, MFA, passkeys or other security settings.",
    ownerId: 'Main account ID',
    ownerIdTip: 'Sub-users enter this in the first field when signing in',
    empty: 'No sub-users yet',
    colName: 'Sub-user name',
    colRemark: 'Remark',
    colStatus: 'Status',
    colLastLogin: 'Last sign-in',
    colCreated: 'Created',
    status_active: 'Enabled',
    status_disabled: 'Disabled',
    never: 'Never',
    resetPassword: 'Reset password',
    disable: 'Disable',
    enable: 'Enable',
    namePlaceholder: 'e.g. ops, dev.zhang',
    nameRequired: 'Enter a sub-user name',
    nameRule: "3–32 letters, digits, '_', '.' or '-', starting with a letter or digit",
    password: 'Password',
    passwordRequired: 'Enter a password',
    newPassword: 'New password',
    generate: 'Generate',
    remarkPlaceholder: 'e.g. Zhang from operations',
    remarkTooLong: 'At most {max} characters',
    permissionNote: 'A sub-user has the same permissions as the main account (no permission classes yet). Only create one for someone you trust.',
    editTitle: 'Edit sub-user {name}',
    passwordTitle: "Reset {name}'s password",
    passwordNote: "All of this sub-user's signed-in sessions end at once.",
    issuedTitle: 'Sub-user sign-in details',
    issuedWarn: 'The password is shown only this once. Copy it and hand it to the sub-user safely.',
    loginUrl: 'Sign-in page',
    copyAll: 'Copy sign-in details',
    copyFailed: "Couldn't copy; select the text and copy it",
    done: 'Done',
    saved: 'Saved',
    enabled: 'Sub-user {name} enabled',
    disabledDone: 'Sub-user {name} disabled; its sessions have ended',
    disableTitle: 'Disable sub-user {name}?',
    disableConfirm: "The sub-user can't sign in while disabled, and its signed-in sessions end at once. You can enable it again any time.",
    deleteTitle: 'Delete sub-user {name}?',
    deleteConfirm: "The sub-user can't sign in any more and its signed-in sessions end at once. This can't be undone.",
    deleted: 'Sub-user {name} deleted',
    createFailed: "Couldn't create it",
    saveFailed: "Couldn't save",
    deleteFailed: "Couldn't delete it",
  },
  subSecurity: {
    tag: 'Sub-user',
    signedInto: 'Signed in under main account {name}',
    owner: 'Main account',
    remark: 'Remark',
    lastLogin: 'Last sign-in',
    passwordDesc: "The sub-user's own password. Changing it signs this sub-user out on other devices.",
    passwordNote: 'This browser stays signed in; sign-ins on other devices end at once.',
    scope: "A sub-user has the same permissions over resources as the main account; the main account's password, MFA, passkeys, sign-in records and sub-users are managed by the main account only.",
  },
  securityCenter: {
    title: 'Security Center',
    accountId: 'Account ID: {id}',
    level: 'Security level',
    level_low: 'Low',
    level_medium: 'Medium',
    level_high: 'High',
    tipMfa: 'With an MFA device, a leaked password alone cannot sign in',
    tipPassword: 'Your password is more than {days} days old; consider changing it',
    tipGood: 'Every safeguard is on',
    recentFails: '{n} failed sign-ins in the last {days} days. If they were not you, change your password and bind an MFA device now.',
    email: 'Email',
    phone: 'Phone',
    registered: 'Signed up',
    lastLogin: 'Last sign-in',
    unbound: 'Not set',
    settingsTitle: 'Security settings',
    password: 'Password',
    passwordDesc: 'Change it from time to time, and do not use it on other sites.',
    passwordChanged: 'Last changed {time}.',
    mfa: 'MFA device',
    mfaOnDesc: 'Bound: signing in asks for the 6-digit code from your authenticator app. Bound {time}.',
    mfaOffDesc: 'Not bound. Once bound, signing in asks for a code from the authenticator app on your phone as well as the password.',
    emailDesc: '{email}, for ticket replies and platform notices.',
    sessions: 'Other sessions',
    sessionsDesc: 'Signed in on someone else\'s computer, or think the account is in other hands? End every session but this browser\'s, at once.',
    change: 'Change',
    bind: 'Bind',
    unbind: 'Unbind',
    endOthers: 'Sign out other devices',
    logsTitle: 'Sign-in history',
    logsSub: 'Last {n}',
    colTime: 'Time',
    colResult: 'Result',
    colIp: 'IP address',
    colDevice: 'Device',
    colMethod: 'Method',
    method_password: 'Password',
    method_code: 'Code',
    method_passkey: 'Passkey',
    method_sub_user: 'Sub-user {name}',
    passkeys: 'Passkeys',
    passkeysOnDesc: '{n} passkey(s) added. Sign in with your fingerprint, face or device PIN — no password needed.',
    passkeysOffDesc: 'Sign in with your fingerprint, face or device PIN instead of a password. The private key never leaves your device.',
    manage: 'Manage',
    add: 'Add',
    result_success: 'Signed in',
    result_bad_password: 'Wrong password',
    result_bad_mfa: 'Wrong code',
    result_inactive: 'Account disabled',
    unknownDevice: 'Unknown device',
    logsEmpty: 'No sign-ins yet',
    saveFailed: 'Could not save',
    passwordTitle: 'Change password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'Confirm new password',
    currentRequired: 'Enter your current password',
    newRequired: 'Enter a new password',
    confirmRequired: 'Enter the new password again',
    passwordRule: '{min}–{max} characters, with at least two of letters, digits and symbols',
    passwordSame: 'The new password must differ from the current one',
    passwordMismatch: 'The passwords do not match',
    passwordNote: 'Sessions on other devices end at once; this browser stays signed in.',
    strength: 'Strength',
    strength_1: 'Weak',
    strength_2: 'Fair',
    strength_3: 'Strong',
    passwordDone: 'Password changed; other devices are signed out',
    emailTitle: 'Change email',
    phoneTitle: 'Change phone',
    phoneBindTitle: 'Add phone',
    emailHint: 'Ticket replies and platform notices go to this address',
    emailRequired: 'Enter an email address',
    emailInvalid: 'Enter a valid email address',
    loginPassword: 'Password',
    passwordRequired: 'Enter your password',
    confirmWithPassword: 'Enter your password to confirm it is you',
    contactDone: 'Saved',
    mfaTitle: 'Bind an MFA device',
    mfaStep1: 'Install an authenticator app on your phone, such as Google Authenticator, Microsoft Authenticator or Tencent Authenticator.',
    mfaStep2: 'Scan the QR code below with the app to add the account.',
    mfaManual: 'Cannot scan? Enter this key in the app:',
    mfaStep3: 'Enter the 6-digit code the app shows and your password to finish.',
    mfaCode: 'Verification code',
    mfaCodeHint: 'Open your authenticator app and enter the 6 digits it shows',
    mfaCodeRequired: 'Enter the 6-digit code',
    mfaDone: 'MFA device bound; signing in will ask for a code',
    mfaUnbindTitle: 'Unbind MFA device',
    mfaUnbindWarn: 'Once unbound, signing in takes only the password, and the security level drops. If the device is lost and gives no code, ask the platform administrator to reset it.',
    mfaUnbindDone: 'MFA device unbound',
    endOthersTitle: 'Sign out other devices',
    endOthersConfirm: 'Every session of this account except this browser\'s ends at once and must sign in again. Continue?',
    endOthersDone: 'Other devices are signed out',
    tipSecondStep: 'With an MFA device or text message sign-in protection, a leaked password alone cannot sign in',
    loginSms: 'Sign-in protection',
    loginSmsOnDesc: 'On: signing in asks for a code texted to {phone}.',
    loginSmsOffDesc: 'Off. Once on, signing in asks for a code texted to your verified phone as well as the password.',
    loginSmsNeedsPhone: 'Off. Verify your phone first; then signing in can ask for a texted code.',
    loginSmsNeedsPhoneFirst: 'Verify your phone first',
    turnOn: 'Turn on',
    turnOff: 'Turn off',
    verify: 'Verify',
    phoneOffSmsDesc: 'Not set. Once added and verified, it protects sign-in and resets the password.',
    phoneProvedDesc: '{phone}, verified: it protects sign-in and resets the password.',
    phoneUnprovedDesc: '{phone}, not verified. Verify it to protect sign-in and reset the password with it.',
    phoneInvalid: 'Enter a valid mobile number',
    phoneVerifyTitle: 'Verify phone',
    phoneSmsHint: 'Choose the country code, then the number. Save it empty to remove the number',
    phoneDone: 'Phone verified',
    phoneRemoved: 'Phone removed',
    loginSmsOnTitle: 'Turn on sign-in protection',
    loginSmsOffTitle: 'Turn off sign-in protection',
    loginSmsOnNote: 'Once on, every sign-in asks for a code texted to {phone} as well as the password.',
    loginSmsOffWarn: 'Once off, signing in takes only the password, and the security level drops.',
    loginSmsOnDone: 'Sign-in protection on',
    loginSmsOffDone: 'Sign-in protection off',
    result_bad_sms: 'Wrong text code',
    result_bad_email_code: 'Wrong email code',
    result_password_reset: 'Password reset by text',
    phoneNoSmsDesc: 'Not set. Once the platform turns text messages on, bind a number with a texted code.',
    phoneUnprovedNoSmsDesc: '{phone}, not verified. Once the platform turns text messages on, verify it with a texted code.',
  },
  passkeys: {
    title: 'Passkeys',
    addTitle: 'Add a passkey',
    intro: "Passkeys let you sign in with your fingerprint, face or device PIN instead of a password. The private key stays on your device; the platform keeps only the public key, so phishing sites can't use it.",
    blocked_browser: "This browser doesn't support passkeys. Update it, then add one",
    blocked_domain: 'Passkeys need the console opened on a domain over HTTPS; this address cannot add or use them',
    colName: 'Name',
    colAdded: 'Added',
    colUsed: 'Last used',
    rename: 'Rename',
    deleteConfirm: "You won't be able to sign in with it any more. Remove the passkey saved on the device from the device's password manager.",
    empty: 'No passkeys yet',
    count: '{n} of {max} added',
    add: 'Add passkey',
    close: 'Close',
    name: 'Name',
    nameHint: 'To tell your devices apart, e.g. "Office MacBook"',
    nameRequired: 'Enter a name',
    nameTooLong: 'At most {max} characters',
    addNote: 'After Continue, follow your browser to verify with your fingerprint, face or device PIN.',
    continue: 'Continue',
    added: 'Passkey "{name}" added',
    deleted: 'Passkey "{name}" deleted',
    cancelled: 'Adding the passkey was cancelled',
    deviceHasOne: 'This device already holds a passkey for this account',
    addFailed: "Couldn't add the passkey",
    renameFailed: "Couldn't rename it",
    deleteFailed: "Couldn't delete it",
  },
  inbox: {
    title: 'Messages',
    settings: 'Message settings',
    settingsTitle: 'Message settings',
    settingsIntro: 'Choose how each kind of message reaches you: in-app messages appear under the bell and in Messages, mail goes to your account email. Changes apply at once.',
    settingsIntroStaff: 'Choose which messages you get in-app, under the bell and in Messages. Ticket mail goes to the notification addresses in Ticket settings (or to the assignee once a ticket has one).',
    prefSaved: 'Saved',
    unreadCount: '{n} unread',
    readAll: 'Mark all read',
    allRead: 'All marked read',
    more: 'View all',
    empty: 'No messages',
    emptyUnread: 'No unread messages',
    attention: '{n} to deal with',
    tabAll: 'All',
    tabUnread: 'Unread',
    categoryAll: 'All types',
    category_ticket: 'Tickets',
    category_billing: 'Billing',
    category_node: 'Nodes',
    markRead: 'Mark read',
    delete: 'Delete',
    deleted: 'Deleted',
    deleteTitle: 'Delete {n} message(s)?',
    deleteContent: 'This cannot be undone.',
    view: 'View',
    detail: 'Message',
    follow: 'Open',
    close: 'Close',
    colTitle: 'Title',
    colCategory: 'Type',
    colTime: 'Time',
    colActions: 'Actions',
    colEvent: 'Message',
    channelInbox: 'In-app',
    channelMail: 'Mail',
    event_ticket_new_staff: 'New ticket',
    event_ticket_reply_staff: 'Customer replied',
    event_ticket_reply_customer: 'Ticket reply',
    event_ticket_resolved: 'Ticket resolved',
    event_ticket_closed: 'Ticket closed',
    event_ticket_auto_closed: 'Ticket auto-closed',
    event_autorenew_ok: 'Auto-renew succeeded',
    event_autorenew_failed: 'Auto-renew failed',
    eventHint_ticket_new_staff: 'When a customer opens a ticket; only the assignee once it has one',
    eventHint_ticket_reply_staff: 'When a customer replies; only the assignee once it has one',
    eventHint_ticket_reply_customer: 'When support replies to your ticket',
    eventHint_ticket_resolved: 'When support replies and closes your ticket',
    eventHint_ticket_closed: 'When your ticket is closed',
    eventHint_ticket_auto_closed: 'When a quiet ticket closes itself',
    eventHint_autorenew_ok: 'After a package renews from your balance',
    eventHint_autorenew_failed: 'When a package could not be renewed',
  },
  serverErrors: {
    emailCode: {
      unavailable: 'Mail is not set up on this platform, so no email code can be sent',
      badEmail: 'Enter a valid email address',
      tooMany: 'Too many codes asked for — try again later',
      wrong: 'The email verification code is not correct',
      expired: 'The email verification code is no longer valid — ask for a new one',
      tooSoon: 'Wait {seconds} seconds before asking for another code',
      dailyLimit: 'This address has had the most codes it may have today ({max}) — try again tomorrow',
    },
    assistant: {
      badBaseURL: 'The API address must start with http:// or https://',
      badDailyLimit: 'The daily limit is 0 (none) to 1000',
      badIcon: 'Unknown icon: {value}',
      badKey: 'The model service refused the API key; check it',
      badLauncher: 'Unknown button shape: {value}',
      badMaxTokens: 'An answer is 256 to 8192 tokens at most',
      badModel: "A model name is letters, digits and . _ : / {'@'} -",
      badName: 'The name is at most {max} characters',
      badPlacement: 'Unknown placement: {value}',
      badProvider: 'Unknown provider: {value}',
      badSuggestion: 'A suggested question is at most {max} characters',
      badTemperature: 'The temperature is 0 to 2',
      baseURLRequired: 'Enter the API address',
      busy: 'The last answer is still being written; wait or stop it',
      dailyLimit: 'You have used today\'s {limit} questions; ask again tomorrow',
      emptyAnswer: 'The model gave no answer; try again',
      emptyQuestion: 'Type a question',
      encryptionKeyMissing: 'Set secrets.encryption_key in control.yaml before saving the API key',
      keyRequired: 'Enter the API key',
      modelRequired: 'Enter the model',
      promptTooLong: 'The instructions are at most {max} characters',
      providerBalance: 'The model service account has no balance left; contact the platform admin',
      questionTooLong: 'A question is at most {max} characters',
      rateLimited: 'The model service is busy; try again shortly',
      timeout: 'The model took too long to answer; try again',
      tooManySuggestions: 'At most {max} suggested questions',
      unavailable: 'The AI assistant is not switched on',
      unreachable: 'The model service could not be reached; try again later',
      upstream: 'The model service answered with an error ({status}): {message}',
    },
    profile: {
      badNickname: 'A nickname is at most {max} characters',
      badBackupName: 'A contact name is at most {max} characters',
      badCompany: 'A company name is at most {max} characters',
      badAddress: 'An address is at most {max} characters',
      badSiteUsage: 'At most {max} characters',
      badRegion: 'Choose a province and a city',
      badQQ: 'A QQ number is 5 to 12 digits',
      badWeChat: 'A WeChat ID is 6 to 20 letters, digits, - or _, starting with a letter, or the mobile number it is bound to',
      badBackupPhone: 'Enter a phone number: digits, with + - ( ) or spaces',
      badIndustry: 'Unknown industry: {value}',
      badTeamSize: 'Unknown team size: {value}',
      badPostcode: 'A postcode is 6 digits',
      badCountry: 'Unknown country or region: {value}',
      countryLocked: 'The country or region cannot change once the account is verified',
      avatarBadImage: 'Upload a PNG, JPEG or GIF picture at least 64 pixels on each side',
      avatarTooLarge: 'A picture is at most {max} MB',
    },
    systemUpdate: {
      noReleaseKey: 'This build has no release public key, so it cannot verify updates',
      upToDate: 'The control plane is up to date',
      noUpdater: 'The updater (cdnx-updater) is not installed on the control plane\'s host',
      busy: 'An update is under way',
      notChecked: 'Check for updates first',
      badReleaseUrl: 'The repository address is not an http(s) address',
      checkFailed: 'The repository could not be read: {detail}',
      badComponent: 'Components are the agent and the edge',
      succeeded: 'Updated the control plane from {from} to {to}',
      rolled_back: 'Updating to {to} failed; {from} is back',
      failed: 'Updating to {to} failed',
      artifactsPreparing: 'The control plane is still downloading the new version for the nodes; try again in a minute',
      artifactsFailed: 'The control plane could not download the new version for the nodes (trying again): {detail}',
    },
    realname: {
      badFee: 'An attempt costs at most {max}',
      badFree: 'An account may have 0 to 100 attempts free',
      badPhone: 'Enter the mainland mobile number in your name',
      badProvider: 'Unknown provider: {value}',
      badRegion: '{value} is not a region like ap-guangzhou',
      badRuleId: 'A RuleId is digits',
      badSecretId: 'A SecretId is 16 to 64 letters and digits',
      badSecretKey: 'That is not a SecretKey',
      insufficientBalance: 'This verification costs {fee} and your balance is {balance}; top up first',
      numberGone: 'This record did not keep the ID number',
      ruleRequired: 'WeChat face verification needs the business rule RuleId',
      tencentKeyRequired: 'Enter the Tencent Cloud SecretId and SecretKey',
      aliyunKeyRequired: 'Enter the Alibaba Cloud AccessKey ID and AccessKey Secret',
      authCodeRequired: '{method} needs the AuthCode of its approved tag',
      badAccessKeyId: 'An AccessKey ID is 12 to 64 letters and digits',
      badAccessKeySecret: 'That is not an AccessKey Secret',
      badAuthCode: 'An AuthCode is 4 to 64 characters, no spaces',
      unavailable: 'Online real-name verification is not open on this platform',
      already: 'This account is verified already',
      badMethod: 'Choose one of the verification methods offered',
      badName: 'Enter your name as it is on your identity card',
      badCertNo: 'Enter a valid 18-digit resident identity card number',
      startFailed: 'The verification could not be started; try again later',
      notFound: 'No such verification',
      tooMany: '{max} verifications have been started today, the most allowed; try again tomorrow',
      identityTaken: 'This identity card has verified {max} accounts already, the most allowed',
      unknownMethod: 'Unknown verification method: {value}',
      noMethods: 'Show at least one method on the list',
      badDefault: 'The default method must be one shown on the list',
      duplicateMethod: '{method} is on the list already',
      sumsubKeyRequired: 'Enter the Sumsub app token and secret key',
      levelRequired: 'Enter the Sumsub verification level\'s name',
      veriffKeyRequired: 'Enter the Veriff API key and shared secret key',
      badAppToken: 'That is not a Sumsub app token',
      badLevelName: 'A level name is letters, digits, spaces, . _ or -',
      badVeriffKey: 'That is not a Veriff API key',
      badSumsubSecret: 'That is not a Sumsub secret key',
      badVeriffSecret: 'That is not a Veriff shared secret key',
      badDaily: 'An account may start 1 to 20 verifications a day',
      badMaxAccounts: 'One identity card may verify 1 to 10 accounts',
      encryptionKeyMissing: 'Set secrets.encryption_key in control.yaml before opening real-name verification',
    },
    notify: {
      unknownEvent: 'There is no such message type',
    },
    msgtpl: {
      badSubject: 'A subject is 1 to 200 characters',
      badTitle: 'A heading is 1 to 100 characters',
      badBody: 'The body is at most 2000 characters',
      badButton: 'A button says 1 to 20 characters',
      badInboxTitle: 'An in-app message title is 1 to 100 characters, in both Chinese and English',
      badInboxBody: 'An in-app message body is at most 1000 characters in each language',
      unknownVar: "This mail has no value {name}",
      unknownEvent: 'No such message: {value}',
    },
    captcha: {
      required: 'Complete the human verification first',
      failed: 'The human verification did not pass. Try again',
      unavailable: 'Human verification is unavailable right now. Try again later',
      changed: 'The verification method has changed. Verify again',
      busy: 'Too many verification requests. Try again in a minute',
      incomplete: 'Fill in every key of the selected service before switching human verification on',
      unknownProvider: 'Unsupported verification service: {value}',
      unknownOnError: 'Unsupported failure policy: {value}',
      unknownLoginMode: 'A sign-in asks never, after wrong passwords, or every time',
      failuresRange: 'Wrong passwords must be between {min} and {max}',
      badTencentAppId: 'A CaptchaAppId is digits',
      badSecretId: 'A SecretId is 16 to 64 letters and digits',
      badGeeTestId: 'A GeeTest ID is 16 to 64 letters and digits',
      badSiteKey: 'A reCAPTCHA site key is 20 to 64 letters, digits, - and _',
      badHost: 'reCAPTCHA loads from www.recaptcha.net or www.google.com',
      badSecret: 'That is not a valid secret key',
      encryptionKeyMissing: 'Set secrets.encryption_key in control.yaml before storing a key',
    },
    sms: {
      unavailable: 'Text messages are not set up on this platform',
      badPhone: 'Enter a valid mobile number',
      intlUnavailable: 'Codes cannot be texted to numbers outside mainland China here',
      intlTemplateRequired: 'Set the international Alibaba Cloud template CODE in Message templates → SMS → Default template first',
      smsbaoKeyRequired: 'Enter the 短信宝 username and password or ApiKey',
      badSmsbaoUser: "A 短信宝 username is letters, digits, _ . {'@'} or -",
      badSmsbaoTemplate: "The message must contain {'{'}code{'}'} where the code goes, and not run too long",
      tooSoon: 'Wait {seconds} seconds before asking for another code',
      dailyLimit: 'This number has had its {max} codes for today. Try again tomorrow',
      tooMany: 'Too many codes asked for. Try again later',
      sendFailed: 'The text message could not be sent. Try again later',
      codeWrong: 'The text message code is not correct',
      codeExpired: 'The text message code is no longer valid. Ask for a new one',
      providerError: 'Alibaba Cloud SMS answered {code}: {message}',
      unreachable: 'Could not reach the SMS provider: {reason}',
      badProvider: 'Unknown SMS provider: {value}',
      badAccessKey: 'An AccessKey ID is letters and digits only',
      badTemplate: '{value} is not a template code like SMS_123456789',
      badTemplateParam: '{value} is not a template variable name',
      badSign: 'A signature is at most 32 characters',
      badTTL: 'A code lasts 1–30 minutes',
      badInterval: 'The wait between codes to one number is 30–600 seconds',
      badDaily: 'A number may get 1–100 codes a day',
      encryptionKeyMissing: 'No encryption key is configured on the control plane (secrets.encryption_key in control.yaml), so the AccessKey secret cannot be saved yet',
      accessKeyRequired: 'Enter the AccessKey ID',
      secretRequired: 'Enter the AccessKey secret',
      signRequired: 'Enter the signature',
      templateRequired: 'Set the mainland Alibaba Cloud template CODE in Message templates → SMS → Default template first',
    },
    account: {
      wrongPassword: 'The password is not correct',
      passwordSame: 'The new password must differ from the current one',
      passwordWeak: 'A password needs {min}–{max} characters, with at least two of letters, digits and symbols',
      emailInvalid: 'Not a valid email address',
      emailTaken: 'Another account uses this email address',
      phoneInvalid: 'Not a valid phone number',
      mfaUnavailable: 'The platform has no encryption key configured, so MFA devices cannot be bound yet. Contact the platform administrator',
      mfaBound: 'An MFA device is already bound',
      mfaNotBound: 'No MFA device is bound',
      mfaSecretInvalid: 'The key is not valid. Close the window and bind again',
      phoneNeedsCode: 'A phone number is bound with a texted code',
      phoneNotVerified: 'Verify your phone first',
    },
    finance: {
      accountExists: 'This Alipay account has been added already',
      accountFormat: 'An Alipay account is an email address or an 11-digit mobile number',
      accountIncomplete: 'Receiving account {n} needs an account name, a bank and an account number',
      accountName: 'Enter the real name on the Alipay account',
      accountTooLong: 'Receiving account {n} has a field that is too long',
      approveAmount: 'Approve between ¥0.01 and ¥{max}',
      autoRenewCancelled: 'A cancelled package is not renewed automatically',
      badFee: 'The fee rate is 0‰–1000‰ and the minimum fee cannot be negative',
      badMin: 'A minimum must be between ¥0.01 and ¥{max}',
      badRefundDays: 'Refund windows are 0–{max} days',
      badUpload: 'The attachment was not found. Upload it again',
      badUploadPurpose: 'Unsupported upload',
      badUploadType: 'Only PNG, JPEG and WebP images and PDF files are accepted',
      badWithdrawRange: 'Withdrawals must be at least ¥0.10, at most ¥{max} and no less than the minimum',
      email: 'The email address is not valid',
      emailRequired: 'An electronic invoice needs an email address. Add one to the invoice title first',
      emptyUpload: 'The file is empty',
      expressRequired: 'Enter the courier and the tracking number',
      feeOverMin: 'At the minimum withdrawal, the fee would leave less than ¥0.10 to transfer. Lower the fee or raise the minimum',
      insufficientBalance: 'Your balance is ¥{balance} and this costs ¥{amount}. Top up first',
      invoiceBadProfile: 'Choose an invoice title',
      invoiceClosed: 'Invoices cannot be requested',
      invoiceFileRequired: 'Upload the electronic invoice',
      invoiceMedium: 'This invoice delivery is not offered',
      invoiceMin: 'An invoice must be for at least ¥{min}',
      invoiceNoKind: 'Offer at least one invoice type and one delivery method',
      invoiceNumber: 'Enter the invoice number (8–24 digits or letters)',
      invoiceType: 'This invoice type is not offered',
      labelTooLong: 'A label can be at most {max} characters',
      mailIncomplete: 'A paper invoice needs a recipient, a phone number and a mailing address. Add them to the invoice title first',
      noteRequired: 'Enter a reason',
      noteTooLong: 'A note can be at most {max} characters',
      overCashable: 'At most ¥{max} can be withdrawn or refunded: invoiced money cannot leave',
      overInvoiceable: 'At most ¥{max} can be invoiced',
      packageNotOnSale: 'This package is not on sale',
      periodUnavailable: 'The package "{package}" is not sold for this period',
      refundAmount: 'Enter the amount to refund',
      refundBadOrder: 'Choose a paid order',
      refundClosed: 'Refunds cannot be requested',
      refundExpired: 'This order can no longer be refunded',
      refundNoWindow: 'Set a refund window for top-ups or package orders before turning refunds on',
      refundOver: 'At most ¥{max} of this order can be refunded',
      refundReason: 'Enter a reason for the refund, at most {max} characters',
      refundUnavailable: 'Alipay is not set up, so the refund cannot go back to the payer',
      remitAmount: 'A transfer must be between ¥{min} and ¥{max}',
      remitBadAccount: 'Choose the account you transferred to',
      remitBadDate: 'The transfer date must be within the last 90 days',
      remitClosed: 'Bank transfers are not accepted',
      remitNoAccount: 'Add a receiving account before turning bank transfers on',
      remitPayerRequired: 'Enter the name on the account you transferred from',
      remitReceiptRequired: 'Upload the bank receipt of the transfer',
      remitTooLong: 'A field is too long',
      renewConflict: 'This package was renewed just now; refresh to see it',
      reviewNoteTooLong: 'A note can be at most {max} characters',
      specialIncomplete: 'A special invoice needs a company title with its registered address, phone number, bank and account',
      stale: 'This document has changed. Refresh and try again',
      storeClosed: 'Packages cannot be bought online',
      taxNo: 'A company title needs its taxpayer ID (15–20 digits or capital letters)',
      titleKind: 'Choose personal or company',
      titleRequired: 'Enter the invoice title, at most 100 characters',
      titleTooLong: 'A field is too long',
      tooManyAccounts: 'At most {max} receiving accounts',
      tooManyPending: '{max} requests are already being processed. Wait for them before submitting another',
      tooManyProfiles: 'At most {max} invoice titles',
      tooManyWithdrawAccounts: 'At most {max} withdrawal accounts',
      transferUnavailable: 'Alipay must use certificate signing to send transfers',
      uploadTooLarge: 'The file cannot be larger than {max} MB',
      withdrawAmount: 'A withdrawal must be between ¥{min} and ¥{max}',
      withdrawBadAccount: 'Choose a withdrawal account',
      withdrawClosed: 'Withdrawals are not available',
    },
    ticket: {
      confidentialClose: 'A confidential message is destroyed when the ticket closes, so it cannot close the ticket',
      badAssignee: 'Choose someone from support',
      badAttachment: 'An attached file is gone or not yours. Upload it again',
      badAutoClose: 'Auto-close is 0–{max} days',
      badCategory: 'Choose a category',
      badDomain: 'Choose one of your sites',
      badEmail: '"{value}" is not a valid email address',
      badPriority: 'Choose a priority',
      badRating: 'Rate from 1 to 5 stars',
      badUploadType: 'Only images, PDF, text and archive files are accepted',
      bodyRequired: 'Write something',
      bodyTooLong: 'A message is at most {max} characters',
      categoryName: 'Category {n} needs a name of at most 20 characters',
      categoryRequired: 'Turn on at least one category to let customers file tickets',
      closed: 'The ticket is closed and cannot be replied to. File a new one if you need to',
      closedAlready: 'The ticket is closed already',
      closedForNew: 'Tickets cannot be filed online. Contact support',
      commentTooLong: 'A comment is at most {max} characters',
      emptyUpload: 'The file is empty',
      notClosed: 'A ticket can be rated once it is closed',
      notOpen: 'Only a ticket waiting on support can be taken on',
      alreadyAccepted: 'Support has taken this ticket on already; reply to it',
      noticeTooLong: 'The notice is at most {max} characters',
      supportNameTooLong: 'The support name is at most {max} characters',
      rated: 'This ticket is rated already',
      templateBody: 'Enter the reply, at most {max} characters',
      templateTitle: 'Enter a name, at most {max} characters',
      titleLength: 'A title is 2–{max} characters',
      tooManyCategories: 'At most {max} categories',
      tooManyEmails: 'At most {max} notification addresses',
      tooManyFiles: 'At most {max} files on a reply',
      tooManyOpen: 'You have {max} tickets open. Close the ones that are solved first',
      tooManyTemplates: 'At most {max} canned replies',
      tooManyUploads: '{max} files are waiting to be sent. Send them or try again later',
      uploadTooLarge: 'A file is at most {max} MB',
    },
    complaint: {
      ticketRequired: 'File a complaint from the page of the ticket it is about',
      badCategory: 'Choose what the complaint is about',
      contentLength: 'Say what happened in {min}–{max} characters',
      badTicket: 'Choose one of your tickets',
      openForTicket: 'This ticket has a complaint being looked into already',
      tooManyOpen: '{max} complaints are being looked into already',
      done: 'The complaint is answered or withdrawn already',
      notPending: 'Only a pending complaint can be taken on',
      resultLength: 'Write the answer, at most {max} characters',
    },
    mail: {
      auth: 'The SMTP server refused the login: {reason}',
      badFrom: '"{value}" is not a valid sender address',
      badHost: '"{value}" is not a valid server name',
      badPort: 'The port is 1–65535',
      badRecipient: '"{value}" is not a valid email address',
      badSecurity: 'Unsupported connection security: {value}',
      badURL: '"{value}" is not a console address like https://cdn.example.com',
      connect: 'Could not reach the SMTP server: {reason}',
      encryptionKeyMissing: 'Set secrets.encryption_key in control.yaml before saving the SMTP password',
      failed: 'Sending failed: {reason}',
      fromRequired: 'Enter the sender address',
      hostRequired: 'Enter the SMTP server',
      notReady: 'Turn mail on and fill in the server and the sender first',
      passwordRequired: 'Enter the password for the username',
      rejected: 'The SMTP server refused the message: {reason}',
      timeout: 'The SMTP server did not answer in time',
      tls: 'The secure connection failed: {reason}',
      tooLong: 'A field is too long',
    },
    license: {
      readOnly: 'The platform is read-only for now, so changes cannot be made. Contact the platform administrator',
      notActive: 'The platform is read-only for now, so changes cannot be made. Contact the platform administrator',
      domainQuota: 'The platform has reached its site limit, so no site can be added for now. Contact the platform administrator',
      nodeQuota: 'The license allows {max} nodes, and there are {used}',
      noRepository: 'The release repository opens to licensed control planes only: activate a license first',
      repoTokenExpired: 'The repository download token has expired: the license has not been renewed for too long',
      noKey: 'This binary has no license key built in, so no license can be verified; release builds have one',
      badPortalUrl: 'The portal address (license.portal_url) is not an http(s) address',
      notActivated: 'This control plane has no license yet',
      codeInvalid: 'No license has this code; check it and try again',
      revoked: 'This license was revoked',
      suspended: 'This license is suspended',
      expired: 'This license term has ended; renew it on the portal',
      notStarted: 'This license has not started yet',
      instancesFull: 'Every instance slot of this license is taken: release one on the portal first',
      instanceUnknown: 'The portal does not know this control plane: activate it again with the license code',
      instanceReleased: 'The portal released this control plane: activate it again with the license code',
      offlineOnly: 'The portal holds an offline license for this control plane: upload the newly issued file',
      clockSkew: 'This host clock is more than 5 minutes off the portal: set the time right (NTP) and try again',
      portalBusy: 'The portal is busy; try again in a moment',
      portalError: 'The portal answered HTTP {status}: {detail}',
      portalUnreachable: 'The portal {portal} cannot be reached: {detail}',
      portalBadAnswer: 'The portal answer cannot be used: {detail}',
      stateUnwritable: 'The license files cannot be written: {detail}',
      instanceCorrupt: 'The instance key {path} is damaged: fix or remove it by hand (removed, this is a new instance)',
      noStateDir: 'No license directory is configured (license.dir)',
      fileInvalid: 'This is not a valid license file, or its signature does not verify',
      unbound: 'This license is bound to no instance: licenses now come from the portal, activated with a license code',
      otherInstance: 'This license is for instance {instance}, not this one ({self})',
      notOffline: 'This is not an offline license file: activate an online license with its code',
      fileExpired: 'This license file has expired',
      offlineNoRenew: 'An offline license does not renew itself: upload the file the portal issues next',
    },
    billing: {
      adjustTooLarge: 'One adjustment can be at most ¥{max}',
      adjustZero: 'The amount cannot be zero',
      amountRange: 'A top-up must be between ¥{min} and ¥{max}',
      channelError: 'Alipay could not start the payment: {reason}',
      insufficientBalance: 'The balance is ¥{balance} and cannot go below zero',
      methodUnavailable: 'This payment method is not available. Try another one',
      noteRequired: 'Enter a reason for the adjustment',
      noteTooLong: 'The reason can be at most {max} characters',
      queryFailed: 'Could not query the order at Alipay: {reason}',
      tooManyPending: 'You already have {max} unpaid orders. Try again once they expire ({minutes} minutes)',
      unavailable: 'Online top-up is not available yet',
    },
    payment: {
      alipayPublicKeyRequired: 'Enter the Alipay public key',
      appIDRequired: 'Enter the AppID',
      badAlipayCert: 'The Alipay public key certificate is not a valid RSA certificate',
      badAlipayPublicKey: 'The Alipay public key is not a valid RSA public key',
      badAmountRange: 'The minimum must be at least ¥0.01, and the maximum no more than ¥{max} and no less than the minimum',
      badAppCert: 'The app public key certificate is not a valid RSA certificate',
      badAppID: '"{value}" is not a valid Alipay AppID',
      badBaseURL: '"{value}" is not a valid address. Use a form like https://cdn.example.com',
      badPrivateKey: 'The app private key is not a valid RSA private key',
      badRootCert: 'The Alipay root certificate contains no RSA certificate',
      badSignMode: 'Unsupported signing method: {value}',
      baseURLRequired: 'Set the customer console address before enabling Alipay',
      certKeyMismatch: 'The app public key certificate does not match the app private key',
      certsRequired: 'Certificate mode needs all three certificates: app public key, Alipay public key and Alipay root',
      encryptionKeyMissing: 'The control plane has no encryption key (secrets.encryption_key in control.yaml), so the app private key cannot be saved',
      noProduct: 'Turn on at least one payment product',
      presetOutOfRange: 'The quick amount ¥{value} is outside the allowed range',
      privateKeyRequired: 'Enter the app private key',
      subjectTooLong: 'The order title can be at most {max} characters',
      tooManyPresets: 'You can add at most {max} quick amounts',
    },
    credential: {
      nameTooLong: 'The note is at most {max} characters',
    },
    clickhouse: {
      addrRequired: 'Enter the server address',
      badAddr: '"{addr}" is not host:port',
      badDatabase: '"{database}" cannot be a database name (letters, digits and _, not starting with a digit)',
      badDays: '{table}: keep between {min} and {max} days',
      badHigh: 'The cleanup threshold must be between {min}% and {max}%',
      badKeep: 'Always keep between {min} and {max} days',
      badLow: 'Clean down to between {min}% and {max}% (at least 5% under the threshold)',
      busy: 'Another control plane replica is cleaning; try again in a minute',
      connectFailed: 'Cannot connect to ClickHouse: {error}',
      noDatabase: 'The server has no database {database}; save with "Initialize tables" to create it',
      notConfigured: 'No ClickHouse server is configured',
      notConnected: 'ClickHouse is not connected',
      schemaFailed: 'Creating the tables failed: {error}',
      unknownTable: 'Unknown table: {table}',
    },
    quota: {
      topUpEmpty: 'Enter some traffic or requests to add',
    },
    consoleTLS: {
      badSite: 'Unknown console',
      certInUse: 'Visitors are redirected to HTTPS on this certificate. Turn the redirect off first.',
      domainRequired: 'Enter and save this console\'s domain first',
      lockFromOtherHost: 'Open the admin console at {domain} first, then turn this on (this page was opened at {host})',
      noUsableCert: '{domain} has no valid certificate yet; issue or upload one before redirecting to HTTPS',
      sameDomain: 'The two consoles need different domains',
    },
    controlTLS: {
      badDomain: '"{value}" is not a domain name (no addresses or wildcards)',
      badMode: 'Unknown transport: {value}',
      badPort: 'The port must be 1-65535',
      certExpired: 'The certificate is not valid now (valid until {not_after})',
      certInUse: 'Nodes are using this certificate. Set node transport to Off first.',
      certNotForDomain: 'The certificate covers {names}, not {domain}',
      domainChanged: 'The domain changed while the certificate was being issued; issue again',
      domainRequired: 'Enter and save the communication domain first',
      emailRequired: 'An email address is required to issue a certificate',
      httpsRequired: 'This control plane only accepts node traffic over HTTPS',
      noUsableCert: 'No certificate valid for this domain yet; issue or upload one first',
      nodesOnHTTP: '{count} online node(s) still use HTTP: {nodes}',
    },
    cert: {
      notYours: 'That certificate belongs to another account',
      bindingUnavailable: 'Per-hostname certificates are not enabled on this platform',
      expired: 'The certificate expired on {not_after}',
      invalidPEM: 'The certificate is not a valid PEM-encoded X.509 certificate',
      keyMismatch: 'The private key does not belong to this certificate',
      notCovering: 'The certificate covers {names}, not {host}',
      notUsable: 'Certificate #{id} is expired or revoked',
    },
    linePool: {
      lineRequired: 'Choose a resolve line',
      lineUnsupported: 'The DNS plan of {zone} does not offer the {line} line — upgrade the plan to use it',
      wrongGroup: 'That pool belongs to another line group',
      roleInvalid: 'Role must be primary or backup (got {value})',
    },
    acme: {
      emailRequired: 'An email address is required',
      emailRequiredForZerossl: 'ZeroSSL needs an email address before EAB credentials can be obtained',
      issuanceInFlight: 'An issuance for {domain} is already in flight',
      issuanceOnAnotherReplica: 'Another control-plane replica is issuing this certificate',
      tosRequired: 'You must agree to the CA\'s terms of service',
      unknownCA: 'Unknown certificate authority {value}',
      zerosslNoStaging: 'ZeroSSL has no staging environment — issue against production, or use Let\'s Encrypt to test',
      notConfigured: 'Certificate issuance is not configured on this platform',
      challengeNotServed: 'The CA got {status} when fetching the challenge file for {domain} (it asked {ip}). Usually the file had not reached that node yet, or that node is not the one DNS points at.',
      challengeUnreachable: 'The CA could not reach {domain} on port 80 — check the node firewall and the DNS records',
      rateLimited: 'The CA is rate-limiting {domain}; try again later (repeated failures also trigger this)',
      dnsProblem: 'The CA could not resolve {domain} — make sure its DNS records are live first',
      caaForbids: 'A CAA record on {domain} forbids this CA from issuing',
      issueFailed: 'Issuing a certificate for {domain} failed: {detail}',
    },
    auth: {
      badCredentials: 'Incorrect username or password',
      inactiveUser: 'This account is not active',
      wrongPortal: 'This account cannot sign in through this console',
      throttled: 'Too many attempts. Try again in {minutes} minutes',
      badMfaCode: 'The code is wrong or already used. Enter a new one',
      mfaTicketExpired: 'Verification timed out. Enter your password again',
      resetUnavailable: 'This account cannot reset its password by text message (no verified phone). Ask the platform administrator',
      codeLoginOff: 'Sign-in by code is not offered',
      codeLoginUnavailable: 'No account here signs in with a code sent there — check it, or sign in with your password',
      subBadCredentials: 'The main account, sub-user name or password is incorrect',
      passkeyOff: 'Passkey sign-in is not available',
      mainAccountOnly: 'Only the main account can do this; sub-users cannot',
    },
    passkey: {
      needsDomain: 'Passkeys need the console opened on a domain over HTTPS (IP addresses are not supported)',
      originMismatch: "The page's address does not match the console's; refresh and try again",
      expired: 'The passkey request timed out; try again',
      failed: 'The passkey could not be verified; try again',
      unknown: 'This passkey is not registered here and may have been deleted. Sign in with your password and add it again',
      exists: 'This passkey has already been added',
      limit: 'An account can have at most {max} passkeys; delete one first',
      nameInvalid: 'Name the passkey, at most {max} characters',
    },
    subUser: {
      nameInvalid: "A sub-user name is 3–32 letters, digits, '_', '.' or '-', starting with a letter or digit",
      nameTaken: 'A sub-user with this name already exists',
      limit: 'An account can have at most {max} sub-users',
      remarkTooLong: 'A remark is at most {max} characters',
      badStatus: 'A sub-user is either enabled or disabled',
    },
    cacheJob: {
      noNodes: 'No active nodes to send this to',
      noTargets: 'Give at least one target',
      quota: 'Too many cache jobs in the last hour',
      scheduleNotAllowed: 'Only a prefetch can be scheduled',
      scheduleTooFar: 'A prefetch can be scheduled at most {days} days ahead',
      targetHasDotDot: '{value} must not contain ..',
      targetNotURL: '{value} is not a URL or a path starting with /',
      targetOffSite: '{value} is not on {host} — a job can only touch its own site',
      tooManyTargets: 'At most {max} targets per job, got {got}',
      unknownKind: 'Unknown cache job kind {value}',
    },
    cnameZone: {
      hasGroups: '{count} line group(s) use this domain — point them elsewhere first',
      inUse: '{count} line group(s) use {domain} — move them to another domain first',
      invalid: 'Enter an apex domain like example.com — no scheme, path, port or leading dot',
      notApex: 'Enter the base domain only (example.com), not a subdomain of it',
      providerRequired: 'Choose a DNS provider',
      ttlRange: 'TTL must be between {min} and {max} seconds',
      ttlBelowPlan: 'The DNS plan of this zone ({plan}) accepts no TTL below {min} seconds',
    },
    dns: {
      lineGroupNoResolveValue: 'The package\'s line group has no resolve value to point the CNAME at',
      cnameDomainBound: 'CNAME domain {domain} is still bound to package {package}',
      encryptionKeyMissing: 'No encryption key configured — set secrets.encryption_key in control.yaml before storing DNS credentials',
      unsupportedProvider: 'Unsupported DNS provider type {value}',
      cnameZoneMissing: 'Package {package} is bound to CNAME domain #{id}, which cannot be loaded — it may have been deleted',
      lineGroupMissing: 'Package {package} is bound to line group #{id}, which cannot be loaded — it may have been deleted',
      providerUnavailable: 'DNS provider account #{id} cannot be used: {detail}',
      providerListFailed: 'Listing records in {zone} failed: {detail}',
      providerWriteFailed: 'Writing record {name} failed: {detail}',
      cnameNameTaken: '{name} already has a {type} record (value {value}, record id {record_id}), and a CNAME cannot share its name',
      cnameNoZone: '{host} is under none of the platform\'s CNAME domains, so there is no DNS account to remove it through',
      lineGroupValueUnresolvable: 'The line group\'s resolve value {target} has no address in public DNS; sites using it will not be reachable',
      lineValueNameTaken: '{name} already has a {type} record (value {value}, record id {record_id}); a line group\'s resolve value must hold the node A records',
      lineNoActiveNodes: 'Line group {group} has no usable node (an active node with a public IPv4), so {name} resolves to no address',
      lineNoDefaultFallback: '{name} has records only on the {line} line. Resolvers on any other line fall back to the default line, which has none, so they get no answer.',
      supportedLinesFailed: 'Reading the resolve lines {zone} may use failed: {detail}',
    },
    domain: {
      badCityName: "Invalid city name: {value} (at most 64 characters, no {'|'} or line breaks)",
      badOriginHost: '"{value}" is not a valid host name',
      badRealIpHeader: '"{value}" is not a valid header name; use letters, digits and hyphens',
      challengeDifficultyRange: 'Difficulty must be between 8 and 24',
      challengeTTLRange: 'TTL must be between 60 and 86400 seconds',
      hostNotVerified: '{host} has not passed CNAME verification yet; a certificate can be requested once it has',
      siteNotServing: 'The site is disabled, so no certificate can be requested for {host}',
      cnameLookupFailed: 'Looking up {host} in DNS failed: {detail}',
      cnameNotFound: '{host} has no CNAME record yet — add one pointing at {target}',
      cnamePointsElsewhere: '{host} points at {resolved}, not {target}',
      cnameTargetMissing: '{target} does not resolve in public DNS, so the platform\'s CNAME record is not live. Check that the DNS account bound to the CNAME domain is the provider actually serving it',
      exemptPathNotAbsolute: 'Exempt path {value} must start with /',
      exemptPathTooLong: 'Exempt path {value} is too long',
      failBanDurationRange: 'The ban duration must be between {min} and {max} seconds',
      failBanMaxRange: 'The failure count must be between {min} and {max}',
      failBanWindowRange: 'The window must be between {min} and {max} seconds',
      forbidden: 'You do not have access to this site',
      geoBanDurationRange: 'The ban duration must be between {min} and {max} seconds',
      geoBlockNoRegions: 'Select at least one country or region',
      hostNotInSite: '{host} is not one of this site\'s domains',
      hostnameRequired: 'A hostname is required',
      nameRequired: 'A name is required',
      noCnameTarget: 'This site has no CNAME target — its package has no CNAME domain bound',
      originNotAHost: 'The origin address must be a bare hostname or IP — no scheme, port, path or spaces',
      originNotPublic: 'The origin address must be a public host — private, loopback and link-local addresses are not allowed',
      packageNotActive: 'Package {id} is not one of this account\'s active packages',
      packageRequired: 'Choose a package',
      tooManyCities: 'At most {max} cities',
      tooManyExemptPaths: 'At most {max} exempt paths',
      unknownCCLevel: 'Unsupported CC protection level: {value}',
      unknownCCAutoLevel: 'Unsupported adaptive CC sensitivity: {value}',
      unknownCCAutoMode: 'Unsupported adaptive CC verification: {value}',
      unknownChallengeMode: 'Unknown challenge mode {value}',
      unknownGeoBlockAction: 'Unsupported region block action: {value}',
      unknownGeoBlockMode: 'Unsupported region block mode: {value}',
      unknownProxyMode: 'Unsupported proxy mode: {value}',
      unknownRegion: 'Unknown country or region code: {value}',
      unknownSslMode: 'Unsupported SSL mode: {value}',
      unknownSubdivision: 'Unknown province code: {value}',
      upstreamLimitRange: 'Upstream concurrency limit must be between 0 and {max}',
    },
    firewall: {
      badPort: '"{value}" is not a port or port range (1-65535, e.g. 443 or 8000-8100)',
      blockMode: 'Unknown block mode: {mode}',
      noPorts: 'Specified-ports mode needs at least one port',
      limitRange: 'Out of range: must be between {min} and {max}',
      tooManyPorts: 'At most {max} ports or ranges per list',
      unknownScope: 'Unknown ban scope: {scope}',
    },
    pages: {
      badMode: 'Page "{key}" has an invalid mode: {mode}',
      dropNotAllowed: 'Page "{key}" cannot be set to close the connection',
      graceRange: 'The grace period must be between 0 and {max} hours',
      htmlRequired: 'Page "{key}" is set to custom HTML but the HTML is empty',
      htmlTooLarge: 'The HTML of page "{key}" must be at most {max} KB',
      messageTooLong: 'The message of page "{key}" must be at most {max} characters',
      statusRange: 'The status code of page "{key}" must be between 400 and 599',
      titleRequired: 'Page "{key}" is set to custom text and needs a title',
      titleTooLong: 'The title of page "{key}" must be at most {max} characters',
      unknownKey: 'There is no page named "{key}"',
    },
    subscription: {
      daysRange: 'The duration must be between {min} and {max} days',
      expiryBeforeStart: 'The expiry cannot be before the start',
      expiryInPast: 'The expiry must be in the future',
      notCancelled: 'This subscription is not cancelled',
      packageInactive: 'Package "{name}" is off sale and cannot be assigned',
      packageNotFound: 'The package does not exist',
      resumeExpired: 'This subscription has expired; renew it instead',
      userNotFound: 'The user does not exist',
    },
    ipList: {
      badEnforcement: 'Enforcement must be {edge} or {kernel}',
      badKind: 'Kind must be {allow} or {deny}',
      batchTooLarge: 'At most {max} addresses per batch, got {got}',
      full: 'This list is full',
      invalidTarget: 'Not a valid IP address or CIDR',
      noAddresses: 'Give at least one address',
      noteTooLong: 'The note must be at most {max} characters',
      targetTooBroad: 'This range is too broad to block',
      unknownScope: 'Unknown scope {value}',
    },
    node: {
      aliasTooLong: 'The alias must be at most {max} characters',
      cannotInitFromStatus: 'Cannot activate a node in status {status}',
      fingerprintTaken: 'This machine fingerprint is already registered with a different token',
      invalidRegisterToken: 'Invalid register token',
      ipNotPublic: 'This is not a routable public IP address',
      noActiveAgentToken: 'Node {id} has no active agent token',
      notAnIP: '{value} is not an IP address',
      primaryIPImmutable: 'The primary address is discovered from heartbeats and cannot be deleted',
      regionNotRegistered: 'Region {region} is not registered — add it on the Regions page first',
      registerFieldsRequired: 'Both the register token and the fingerprint are required',
      tokenExpired: 'This register token has expired',
      tokenUsed: 'This register token has already been used',
    },
    nodeGroup: {
      cnameZoneRequired: 'Choose a CNAME domain',
      hasMembers: 'This line group still has {count} node(s) — move them to another group first',
      resolveValueNotUnderZone: 'Resolve value {value} must be a subdomain of {zone}',
      resolveValueRequired: 'A resolve value is required',
      lineInvalid: 'Resolve line code {value} is not valid (lowercase letters, digits and underscores only)',
    },
    l4: {
      badStatsRange: 'Unsupported statistics range: {range}',
      badBalance: 'Load balancing must be round robin or source IP hash',
      badProtocol: 'The protocol must be TCP, UDP or TCP+UDP',
      badProxyProtocol: 'PROXY protocol must be off, v1 or v2',
      badRange: 'The port range must lie within {min}–{max}, the first port no higher than the last',
      nameLength: 'The name takes 1 to {max} characters',
      noFreePort: 'No free port is left in the range. Contact support.',
      originFormat: 'Origin {n} ({value}) must be host:port, an IPv6 address in brackets',
      originNotPublic: 'Origin {n} ({value}) is not a public address; private, loopback and link-local addresses cannot be origins',
      originRepeated: 'Origin {n} ({value}) is listed twice',
      originRequired: 'Add at least one origin',
      packageNotActive: 'The package chosen is not one of your active packages',
      packageRequired: 'Choose a package',
      portRange: 'The listen port must be between {min} and {max}',
      portReserved: 'Port {port} is reserved; choose another',
      portTaken: 'Port {port} is taken; choose another or leave it empty to have one assigned',
      remarkLength: 'The remark takes at most {max} characters',
      tooManyOrigins: 'At most {max} origins',
    },
    package: {
      cnameBindingIncomplete: 'Access domain, line group and CNAME value must be set together, or all left empty',
      cnameValueInvalid: 'CNAME value {value} is invalid — use letters, digits and hyphens, with dots between labels',
      cnameZoneNotFound: 'Access domain {id} does not exist',
      nodeGroupNoResolveValue: 'Line group {name} has no resolve value — set one first',
      nodeGroupNotFound: 'Line group {id} does not exist',
      descTooLong: 'The description must be at most {max} characters',
      nameRequired: 'A package name is required',
      unknownCnameMode: 'Unknown CNAME mode {value}',
      valueOutOfRange: '{field} must be between 0 and {max} (0 means unlimited)',
      groupNotFound: 'Package group {id} does not exist',
      noWaf: 'Your package “{package}” does not include WAF. Upgrade the package to use it.',
      noCustomCc: 'Your package “{package}” does not include custom CC rules; the CC protection level is available.',
      noWebsocket: 'Your package “{package}” does not include WebSocket.',
      noHttp3: 'Your package “{package}” does not include HTTP/3.',
      noCustomCert: 'Your package “{package}” does not include uploaded certificates; free certificates are available.',
      noOpenapi: 'None of your packages includes the Open API. Upgrade a package to use API keys.',
      noFeature: 'Your package “{package}” does not include this feature.',
      siteQuota: 'The package “{package}” allows at most {max} sites, and the limit is reached.',
      domainQuota: 'The package “{package}” allows at most {max} domains, subdomains included, and the limit is reached.',
      apexQuota: 'The package “{package}” allows at most {max} apex domains, and the limit is reached.',
      ruleQuota: 'The package “{package}” allows at most {max} rules per site, and the limit is reached.',
      noL4: 'Your package “{package}” does not include App Acceleration. Upgrade the package to use it.',
      l4Quota: 'The package “{package}” allows at most {max} App Acceleration rules, and the limit is reached.',
    },
    packageGroup: {
      descTooLong: 'The description must be at most {max} characters',
      hasPackages: 'This group still has {count} package(s) — move them to another group first',
      nameRequired: 'A group name is required',
      nameTaken: 'A package group with this name already exists',
      nameTooLong: 'The group name must be at most {max} characters',
    },
    publish: {
      nothingToPublish: 'Nothing to publish: the platform has no sites yet',
    },
    region: {
      codeInUse: '{count} node(s) still use {code} — confirm to move them along with the rename, or keep the code and change only the name',
      codeInvalid: 'A region code must be 1-32 characters of letters, digits, \'-\' or \'_\'',
      hasNodes: 'This region still has {count} node(s) — move them to another region first',
      nameRequired: 'A region name is required',
    },
    registration: {
      badUsername: 'A username is 3 to 32 letters, digits, _ . or -, starting with a letter or digit',
      usernameTaken: 'This username is taken',
      emailTaken: 'An account with this email exists already — sign in instead',
      phoneTaken: 'This phone is bound to another account already',
      agreeRequired: 'Read and accept the agreements first',
      closed: 'Registration is not open',
      noDefaultPackage: 'Registration is not configured: no default package',
      passwordTooShort: 'The password must be at least {min} characters',
      rateLimited: 'Too many registrations from this address — try again later',
    },
    rule: {
      aclBadHeader: "\"{value}\" is not a valid header name",
      aclBadMatch: 'The match is malformed',
      aclBadValue: "\"{value}\" is not valid for this rule type",
      aclNoValues: 'The rule matches nothing; add at least one value',
      aclTooManyValues: 'At most {max} values per rule; put more on an IP list',
      aclUnknownAction: 'Unsupported access control action: {value}',
      botBadSignature: 'Give either a pattern or a substring, not both',
      botUnknownAction: 'Unsupported bot rule action: {value}',
      browserTTLRange: 'Browser TTL must be -1 (inherit) or between 0 and {max}',
      ccBadConditions: 'The match conditions are malformed',
      ccBadCountStatus: '"{value}" is not a valid status; use a code like 404 or a class like 5xx',
      ccBadEscalation: 'The escalation tiers are malformed',
      ccBadParams: 'The action parameters are malformed',
      ccDimensionsEmpty: 'CC dimensions must not be empty',
      ccPathNotAbsolute: 'Path "{value}" must start with /',
      ccPathTooLong: 'The path can be at most 512 characters',
      ccStatusRange: 'The response status must be a 4xx or 5xx code',
      ccTTLRange: 'The action duration can be at most {max} seconds',
      ccThresholdRange: 'The threshold must be between 1 and 1000000',
      ccTooManyDimensions: 'At most {max} dimensions',
      ccTooManyTiers: 'At most {max} escalation tiers',
      ccUnknownAction: 'Unsupported action: {value}',
      ccUnknownMethod: 'Unsupported request method: {value}',
      ccUnknownMitigation: 'Unsupported mitigation: {value}',
      ccUnknownPathMode: 'Unsupported path match mode: {value}',
      ccWindowRange: 'The window must be between 1 and 86400 seconds',
      notAStatusCode: '{value} is not an HTTP status code',
      patternEmpty: 'The pattern is empty and would match every request',
      patternInvalid: "\"{value}\" is not a valid regular expression",
      patternTooLong: 'The pattern can be at most {max} characters',
      staleIfErrorRange: 'stale-if-error is out of range',
      staleWhileRevalidateRange: 'stale-while-revalidate is out of range',
      statusTTLRange: 'The cache TTL for status {code} is out of range',
      ttlRange: 'The cache TTL must be between 0 and {max}',
      unknownStatus: 'Unsupported rule status: {value}',
      unsupportedAclType: 'Unsupported ACL type {value}',
      unsupportedCcDimension: 'Unsupported CC dimension {value}',
      wafBadDefinition: 'The WAF rule is malformed',
      wafNoTargets: 'Pick at least one place to inspect',
      wafScoreRange: 'The score must be between {min} and {max}',
      wafUnknownCategory: 'Unsupported WAF category: {value}',
      wafUnknownTarget: 'Unsupported inspection target: {value}',
    },
    settings: {
      unknownLogo: 'There is no such logo',
      logoEmpty: 'The logo file is empty',
      logoTooLarge: 'The logo is at most {max} KB',
      logoBadSvg: 'The SVG could not be read: export it as a plain SVG and try again',
      unknownBackground: 'There is no such background',
      backgroundEmpty: 'The picture is empty',
      backgroundTooLarge: 'The background is at most {max} MB',
      backgroundBadType: 'The background is a PNG, JPG, WebP or SVG picture',
      logoBadType: 'The logo is a PNG, JPG, WebP, GIF or SVG picture',
      agreementTooLong: 'An agreement is at most {max} KB',
      footerBadLinkKind: 'Unknown footer link type: {value}',
      footerBadLinkURL: 'Footer link {n}: the address starts with http:// or https://',
      footerCopyrightTooLong: 'The copyright line is at most {max} characters',
      footerLinkNoText: 'Footer link {n} has no name yet',
      footerLinkTextTooLong: 'Footer link {n}: the name is at most {max} characters',
      footerTooManyLinks: 'The footer has at most {max} links',
      loginNoticeEmpty: 'The sign-in page notice is on: write its text',
      noticeTooLong: 'A notice is at most {max} KB',
      registerNoticeEmpty: 'The sign-up page notice is on: write its text',
      badAgreementMode: 'Unknown way of giving the agreement',
      badAgreementURL: 'An agreement link starts with http:// or https://',
      badWebsiteURL: 'The official site starts with http:// or https://',
      mailNotReady: 'Set up Mail first, then ask new accounts to prove their email',
      smsNotReady: 'Set up SMS first, then ask new accounts to prove their phone',
      badNavbarHeight: 'The top bar is {min}–{max} pixels high',
      badMenuWidth: 'The menu is {min}–{max} pixels wide',
      badSkin: '{value} is not one of the theme colours',
      badLevelBadge: 'That is not one of the VIP badge styles',
      defaultPackageRequired: 'Set a default package before opening registration',
      faviconEmpty: 'The icon file is empty',
      faviconNotIco: 'The icon must be a .ico file',
      faviconTooLarge: 'The icon must be at most {max} KB',
      tooLong: '{field} must be at most {max} characters',
    },
    stats: {
      unavailable: 'Statistics need ClickHouse, which is unavailable',
    },
    task: {
      autoRenewed: '{package} renewed from the balance for ¥{amount}, now until {until}',
      autoRenewError: 'The renewal could not be made: a database error; try again later',
      autoRenewGone: 'Subscription #{id} no longer exists',
      autoRenewNotDue: 'Nothing to renew: the package runs until {until}',
      autoRenewOff: 'The customer turned auto-renew off; nothing was charged',
      badResultStatus: 'A task result status must be success or failed, got {value}',
      certDeleted: 'Certificate #{cert_id} no longer exists',
      certIssued: 'Certificate for {domain} issued by {issuer}',
      certRenewed: 'Certificate for {domain} renewed by {issuer}',
      clickhouseDroppedBoth: 'Deleted {days} day(s) of data and emptied {logs} system log table(s), {size} freed',
      clickhouseDroppedDays: 'Deleted {days} day(s) of data, {size} freed',
      clickhouseDroppedLogs: 'Emptied {logs} system log table(s), {size} freed',
      clickhouseFailed: 'The cleanup failed: {error}',
      clickhouseNothing: 'Nothing needed deleting',
      clickhouseStillFull: 'The disk is still over the target: what is left is within the days always kept, or the disk holds other data',
      l4RuleDeleted: 'App Acceleration rule #{id} no longer exists',
      l4CnameNotBound: 'The rule’s package no longer hands out a CNAME (access domain, line group or CNAME value is unset)',
      cnameNotBound: 'The site\'s package no longer hands out a CNAME (CNAME domain, line group or CNAME value is unset)',
      aliasActivated: '{host} verified; the nodes now serve it',
      aliasAlreadyActive: '{host} is already verified',
      aliasDeleted: 'The alias {host} was deleted',
      cnamePublished: 'Published {host} → {target} ({action})',
      cnameRemoved: 'The site was deleted; removed its CNAME record {host}',
      cnameAlreadyGone: '{host} was already gone; nothing to remove',
      cnameStillUsed: '{host} is still used by {user}, so it was left in place',
      internal: 'Internal error: {detail}',
      lineGroupDeleted: 'Line group #{id} no longer exists',
      lineNotBound: 'The line group has no resolve value and CNAME domain to publish',
      linePublished: '{host} now resolves to {count} node address(es)',
      linePublishedWithBackup: '{host} now resolves to {count} address(es); {lines} line(s) fell back to standby addresses',
      lineUnchanged: '{host} is already up to date: {count} node IP(s), nothing changed',
      lineUnchangedWithBackup: '{host} is already up to date: {count} IP(s), {lines} line(s) still on standby addresses',
      notSystemTask: 'Only system tasks can be retried',
      renewNoNames: 'None of the certificate names belongs to {domain} any more; no renewal needed',
      renewNoSite: 'Certificate #{cert_id} belongs to no site; no renewal needed',
      renewSuperseded: 'Already renewed: certificate #{cert_id} covers the same names',
      running: 'The task is running right now; try again shortly',
      siteActivated: '{host} verified; the site is active',
      siteAlreadyActive: 'The site is already {status}; nothing to verify',
      siteDeleted: 'Site #{id} no longer exists',
      unsupportedType: 'Unsupported task type {value}',
      waitingForPublish: 'Waiting for the site\'s CNAME record to be published',
    },
    user: {
      encryptionKeyMissing: 'No encryption key configured — set secrets.encryption_key in control.yaml before storing ID numbers',
      noIdentity: 'This account has no ID number stored',
      passwordRequired: 'A password is required to create a user',
      unknownRealNameStatus: 'Unknown real-name status {value}',
      unknownRealNameType: 'Unknown real-name type {value}',
      unknownStatus: 'Unknown user status {value}',
    },
    member: {
      badExp: 'EXP is 0 to {max}',
      badThresholds: 'The {n} VIP thresholds must rise from at least 1, up to {max}',
      noteRequired: 'Say why: the customer sees it with the change',
      noteTooLong: 'A note is at most {max} characters',
      badChange: 'A change is 1 to {max}',
      expBelowZero: 'The customer has {n} EXP; no more can be taken back',
      noCustomer: 'No such customer; the account may have been deleted',
      growthBelowZero: 'The customer has {n} growth points; no more can be taken away',
    },
    userGroup: {
      hasMembers: 'This user group still has {count} member(s) — move them to another group first',
    },
    deployment: {
      badResultStatus: 'Invalid deployment result status: {value}',
    },
  },
}
